/* Kkumi Club runtime — shared by every kkumi-club-* section so the page can be
 * split into standalone, reorderable theme sections. It targets unique class
 * hooks across the whole document, so it doesn't matter which section each
 * piece of markup lives in. Loaded once (guarded); config comes from the global
 * window.KKUMI_CLUB set by the runtime snippet. */
(function () {
  'use strict';
  if (window.__kkumiClubInit) return;
  window.__kkumiClubInit = true;

  // Shared pure utilities, hoisted to module scope (init()'s render functions
  // close over them). The rest of init() stays nested by design — it's already
  // organised into ~50 named functions that share mutable render state
  // (root, lastProgram, lastBalanceObj, …), so un-nesting them buys nothing.
  function esc(s){ return String(s==null?'':s).replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];}); }
  function uuid(){ return (window.crypto&&crypto.randomUUID)?crypto.randomUUID():('rq-'+Date.now()+'-'+Math.random().toString(36).slice(2)); }
  // Broadcast a store-credit payout so same-page listeners can update without a
  // reload (the cart's credit note). Also parked in sessionStorage as the
  // cross-page hand-off for flows that DO navigate (kkumi-cart-store-credit.js
  // reads + clears `kkumi_credit_flash` on load).
  function announceCredit(cents){
    cents = Number(cents)||0;
    if(cents<=0) return;
    try { sessionStorage.setItem('kkumi_credit_flash', JSON.stringify({ cents:cents, at:Date.now() })); } catch(e) {}
    try { document.dispatchEvent(new CustomEvent('kkumi:credit-added', { detail:{ cents:cents } })); } catch(e) {}
  }

  function init() {
    var root = document;
    var IS_CLUB_PAGE = !!root.querySelector('[data-kkumi-club]');
    var CFG = window.KKUMI_CLUB || {};
    var CC = CFG.copy || {};  // redemption-flow copy (theme settings "Kkumi Club"), with JS fallbacks
    // Merchant copy with {token} substitution. `key` selects the setting, `fb` is
    // the byte-for-byte default, `vars` maps each {token} to its (already-escaped
    // where it lands in innerHTML) live value. The template itself is a trusted
    // merchant setting; callers stay responsible for escaping any live value they
    // splice in (exactly as the hardcoded literals did before).
    function tpl(key, fb, vars){
      var s = (CC[key] != null && CC[key] !== '') ? CC[key] : fb;
      s = String(s);
      if(vars){ for(var k in vars){ if(Object.prototype.hasOwnProperty.call(vars,k)) s = s.split('{'+k+'}').join(vars[k]); } }
      return s;
    }
    // Trailing-arrow CTA label for the page-bottom fallback toast, which
    // builds its markup by concatenation. KKIcons.labelHTML() escapes the
    // label and re-draws a trailing `→` as the shared arrow SVG — the JS twin
    // of snippets/kkumi-label-arrow.liquid.
    function codeActionHTML(){
      var label = tpl('toast_code_action_fallback','Use it now →');
      return (window.KKIcons && window.KKIcons.labelHTML) ? window.KKIcons.labelHTML(label) : esc(label);
    }
    // LOGGED_IN = signed in to a customer account (Liquid's `customer`).
    // Points and rewards are a Kkumi Club perk, though, and Liquid can't see
    // club membership — /balance tells us (`club_member`). Until it lands
    // CLUB_MEMBER is null and a signed-in shopper is treated as a member, so a
    // member never flashes a "join" state; nothing redeemable renders before
    // /balance anyway (affordability needs the balance too). canUse() is the
    // one gate: signed in AND not known to be outside the club.
    var LOGGED_IN = !!CFG.isMember;
    var CLUB_MEMBER = null;
    function canUse(){ return LOGGED_IN && CLUB_MEMBER !== false; }
    var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var BASE = ROOT.replace(/\/$/, '');
    var API = BASE + '/apps/rewards';
    var REGISTER = CFG.register || '/account/register';
    // Where a "join" CTA goes: guests sign in / create an account first; a
    // signed-in customer who hasn't joined goes to the club signup (theme
    // setting "Kkumi Club → Join the club link").
    var JOIN_URL = CFG.joinUrl || '/pages/kkumi-club#kkumi-club-join';
    function joinHref(){ return LOGGED_IN ? JOIN_URL : REGISTER; }

    function api(path, opts){
      opts = opts || {};
      var method = opts.method || 'GET';
      // Route cacheable GETs through the shared client cache (window.KKRewards,
      // assets/kkumi-rewards-cache.js) so the same rewards data isn't refetched
      // on every page when nothing changed. Mutations bypass the cache and
      // invalidate it so the next read re-probes the member version (/balance).
      if (method === 'GET' && window.KKRewards) return window.KKRewards.get(path);
      var p = fetch(API+path, { method:method, headers:opts.body?{'Content-Type':'application/json'}:undefined, body:opts.body?JSON.stringify(opts.body):undefined, credentials:'same-origin' })
        .then(function(r){ return r.json().then(function(j){ if(!r.ok){ var e=new Error(j.error||('HTTP '+r.status)); e.code=j.error; throw e; } return j; }); });
      if (method !== 'GET' && window.KKRewards) p = p.then(function(j){ window.KKRewards.invalidate(); return j; });
      return p;
    }

    // ── Referral attribution (runs on EVERY page, before the boot guard) ──────
    // Share links land anywhere as ?ref=CODE. The visitor usually isn't signed
    // in yet, so stash the code and POST /referral/attach on the first signed-in
    // page view. Attachment is one-shot server-side, so retries are harmless;
    // terminal answers (attached / already referred / bad code) clear the stash.
    var REF_KEY = 'kk:ref:v1', REF_TTL_MS = 30*24*60*60*1000;
    try {
      var refParam = new URLSearchParams(location.search).get('ref');
      if (refParam && /^[A-Z0-9-]{3,32}$/i.test(refParam)) {
        localStorage.setItem(REF_KEY, JSON.stringify({ code: refParam.toUpperCase(), at: Date.now() }));
      }
      var refStash = JSON.parse(localStorage.getItem(REF_KEY) || 'null');
      if (refStash && Date.now() - refStash.at > REF_TTL_MS) { localStorage.removeItem(REF_KEY); refStash = null; }
      if (LOGGED_IN && refStash) {
        api('/referral/attach', { method:'POST', body:{ code: refStash.code } })
          .then(function(){ localStorage.removeItem(REF_KEY); })
          .catch(function(e){
            if (e.code === 'already_referred' || e.code === 'self_referral' || e.code === 'invalid_code') {
              localStorage.removeItem(REF_KEY);
            } // transient errors: keep the stash, retry on the next page view
          });
      }
    } catch (e) { /* storage unavailable — the manual code field still works */ }

    // Beyond attribution, only run on the club page or pages that need
    // program-driven copy substitutions (e.g. the footer club card).
    if (!IS_CLUB_PAGE && !root.querySelector('[data-kk],[data-kk-tier-name]')) return;

    var toastEl = root.querySelector('.kk-toast'); var toastTimer;
    function toast(html){ if(!toastEl) return; toastEl.innerHTML=html; toastEl.classList.add('is-on'); clearTimeout(toastTimer); toastTimer=setTimeout(function(){ toastEl.classList.remove('is-on'); }, 6000); }

    // Re-use the sitewide <cart-toast> (top-right popup, same one that
    // fires on /cart/add) for redemption feedback so the customer sees an
    // instant response in their viewport instead of a hard-to-spot banner
    // at the page bottom. Falls back to kk-toast if the section isn't
    // present (older theme builds).
    function cartToast(){ return document.querySelector('cart-toast'); }
    function redemptionToast(opts){
      var ct = cartToast();
      if (ct && typeof ct.renderMessage === 'function') { ct.renderMessage(opts); return true; }
      return false;
    }
    function hideRedemptionToast(){
      var ct = cartToast();
      if (ct && typeof ct.hide === 'function') ct.hide();
    }

    function set(sel, txt){ var el=root.querySelector(sel); if(el) el.textContent=txt; }
    function setAll(sel, txt){ root.querySelectorAll(sel).forEach(function(el){ el.textContent=txt; }); }

    // Tier badge = the per-tier icon (from the tiers section's SVG) + name,
    // sitting on the balance card / membercard in place of the old generic ✦
    // glyph. Falls back to ✦ when the tiers section isn't on the page or the
    // tier id is unknown — tierIconHtml() handles that. The icon HTML is
    // server-rendered Liquid (trusted); the name is text-set, so even if
    // /balance ever returns admin-editable copy it can't inject markup. */
    function renderTierBadgeEl(el, tier){
      if(!el || !tier) return;
      el.innerHTML = '';
      var iconWrap = document.createElement('span');
      iconWrap.className = 'kk-tier-badge__ic';
      iconWrap.setAttribute('aria-hidden', 'true');
      iconWrap.innerHTML = tierIconHtml(tier.id);
      el.appendChild(iconWrap);
      el.appendChild(document.createTextNode(' '));
      var nameEl = document.createElement('span');
      nameEl.className = 'kk-tier-badge__name';
      nameEl.textContent = tier.name;
      el.appendChild(nameEl);
    }
    function renderTierBadge(sel, tier){ renderTierBadgeEl(root.querySelector(sel), tier); }
    function renderTierBadgeAll(sel, tier){ root.querySelectorAll(sel).forEach(function(el){ renderTierBadgeEl(el, tier); }); }

    var REDEEM_STEP=100, REDEEM_DOLLARS=5, lastBalance=null, lastOptions=null;
    var lastProgram=null, lastBalanceObj=null;
    // The hero membercard progress widget is catalog-driven: "next reward at X pts"
    // is the cheapest fixed-cost reward they can't afford yet (variable rewards have
    // no fixed cost so they're skipped). Tier-locked rewards are skipped too — a
    // member can't redeem them, so they're never "next" or "enough points for".
    // When every reward at their tier is affordable we render a "rewards available"
    // state (naming the tier to reach when higher-tier rewards exist). Wording is
    // deliberately "available / enough points for", not "unlocked": nothing is
    // claimed or permanent until they actually redeem. The bar measures progress
    // between the most expensive affordable reward and the next one.
    function renderProgress(){
      if(lastBalance==null) return;
      var bal=lastBalance;
      var bar=root.querySelector('.js-prog-bar');
      var allFixed=(lastOptions||[]).filter(function(o){ return o.type!=='variable' && o.points; });
      var fixed=allFixed.filter(function(o){ return !o.locked; });
      if(!fixed.length){
        // No catalog yet — fall back to a 100-pt step preview so the widget isn't empty on first paint.
        var step=REDEEM_STEP, ready=bal>=step, inStep=bal%step;
        set('.js-prog-label', ready?tpl('prog_ready_label','Ready to redeem'):tpl('prog_next_label','Next reward'));
        set('.js-prog-frac', (ready?step:inStep)+' / '+step);
        if(bar) bar.style.width=(ready?100:(inStep/step*100))+'%';
        set('.js-prog-note', ready?tpl('prog_ready_note','You can redeem a reward now ✦'):tpl('prog_togo_note','{n} points until your next reward.',{n:(step-inStep)}));
        return;
      }
      fixed.sort(function(a,c){ return a.points-c.points; });
      var next=null, affordable=null;
      for(var i=0;i<fixed.length;i++){
        if(fixed[i].points<=bal) affordable=fixed[i];
        else if(!next) next=fixed[i];
      }
      if(next){
        var prev = affordable ? affordable.points : 0;
        var span = next.points - prev;
        var into = bal - prev;
        var pct = span>0 ? Math.max(0, Math.min(100, into/span*100)) : 0;
        var togo = Math.max(0, next.points - bal);
        set('.js-prog-label', tpl('prog_next_prefix','Next: {label}',{label:next.label}));
        set('.js-prog-frac', bal.toLocaleString()+' / '+next.points.toLocaleString());
        if(bar) bar.style.width = pct + '%';
        set('.js-prog-note', tpl('prog_togo_unlock','{n} points to go',{n:togo.toLocaleString()})+(affordable?tpl('prog_already_unlocked',' · enough points for: {label}',{label:affordable.label}):''));
      } else {
        // Bal >= every fixed reward at their tier. If higher-tier rewards exist,
        // say so and name the lowest tier that opens more of them.
        var reach=lowestLockedTier(allFixed);
        var curTier=(lastBalanceObj && lastBalanceObj.tier && lastBalanceObj.tier.name) || '';
        set('.js-prog-label', reach && curTier
          ? tpl('prog_all_tier_available','All {tier} rewards available ✦',{tier:curTier})
          : tpl('prog_all_unlocked','All rewards available ✦'));
        set('.js-prog-frac', '✓');
        if(bar) bar.style.width = '100%';
        var note = affordable ? tpl('prog_youve_unlocked','You have enough points for: {label}',{label:affordable.label}) : '';
        if(reach) note += (note?' · ':'') + tpl('prog_reach_tier','Reach {tier} for more rewards',{tier:reach});
        set('.js-prog-note', note);
      }
    }
    // Name of the lowest tier that gates any of these (locked) rewards, in
    // program tier order when /program has landed; '' when nothing is locked.
    function lowestLockedTier(opts){
      var locked=opts.filter(function(o){ return o.locked; });
      if(!locked.length) return '';
      var order=(lastProgram && Array.isArray(lastProgram.tiers)) ? lastProgram.tiers.slice().sort(function(a,c){ return (a.min_spend_cents||0)-(c.min_spend_cents||0); }).map(function(t){ return t.id; }) : [];
      locked.sort(function(a,c){ return order.indexOf(a.min_tier)-order.indexOf(c.min_tier); });
      return locked[0].min_tier_name || tpl('rwd_min_tier_fallback','next tier');
    }

    function loadMember(){
      api('/balance').then(function(b){
        if(!b.logged_in) return;
        // An older worker without the field means "member" (pre-club-gate).
        var wasClub = CLUB_MEMBER;
        CLUB_MEMBER = b.club_member !== false;
        if(CLUB_MEMBER && wasClub === null) loadReferral();
        setAll('.js-pts', (b.balance||0).toLocaleString());
        setAll('.js-redeem-bal', (b.balance||0).toLocaleString());
        if(b.tier){ renderTierBadge('.js-tier', b.tier); renderTierBadgeAll('.js-redeem-tier', b.tier); }
        if(b.tier){ var t=root.querySelector('.kk-tier[data-tier="'+b.tier.id+'"]'); if(t) t.classList.add('kk-tier--current'); }
        lastBalance = b.balance || 0; lastBalanceObj = b;
        renderProgress(); renderWallet(); renderGrid();
        maybeClimb(); renderRedeemBal();
        // Birthday bonus is tier-scoped — once we know the member's tier,
        // re-render the program tokens so "birthday-pts"/"birthday-amt" reflect
        // the tier's bonus instead of the base. Idempotent.
        if(lastProgram) applyProgram(lastProgram);
        if(b.birthday) showDobSet(b.birthday);
        if(window.__kkWalletLoad) window.__kkWalletLoad.done('balance');
      }).catch(function(){ if(window.__kkWalletLoad) window.__kkWalletLoad.done('balance'); });

      function loadCodes(){
        var wrap=root.querySelector('.js-codes'); if(!wrap) return;
        var meta=root.querySelector('.js-codes-meta'); if(meta) meta.textContent = tpl('codes_meta_loading','Loading…');
        wrap.innerHTML='<div class="kk-empty">'+tpl('codes_loading','Loading your codes…')+'</div>';
        api('/codes').then(function(d){
          var codes=d.codes||[];
          if(meta) meta.textContent = tpl('codes_meta_ready','{n} ready',{n:codes.length});
          if(!codes.length){ wrap.innerHTML='<div class="kk-empty">'+tpl('codes_empty','No codes yet. Redeem points below.')+'</div>'; return; }
          wrap.innerHTML = codes.map(function(c){
            // Prefer the stamped `discount_cents` (migration 0030) — accurate
            // even when the merchant changes the redeem rate. Older codes
            // (pre-0030) have it NULL; fall back to the live program rate so
            // we don't quote "$50 off" on a $5 code minted before the column
            // existed.
            // Description must reflect the reward TYPE — a free / BOGO / percent
            // code is NOT "$X off your order". Only true $-amount rewards quote a
            // dollar figure (prefer the stamped discount_cents from migration
            // 0030; fall back to the live program rate for older codes). Reward
            // label/title is merchant-controlled → escape it.
            var type = c.reward_type, desc;
            if (type === 'free_product' || type === 'free_collection') {
              desc = esc(c.product_title || c.reward_label || tpl('code_desc_free_gift','Free gift'));
            } else if (type === 'percent') {
              desc = esc(c.reward_label || tpl('code_desc_percent','Percent off'));
            } else if (type === 'bogo') {
              desc = esc(c.reward_label || tpl('code_desc_bogo','Buy one, get one'));
            } else {
              var dollars;
              if (c.discount_cents != null) {
                dollars = Math.floor(Number(c.discount_cents) / 100);
              } else {
                var step = REDEEM_STEP || 100, perStep = REDEEM_DOLLARS || 5;
                dollars = Math.floor(((c.points||0) / step) * perStep);
              }
              desc = tpl('code_desc_dollars_off','{amount} off your order',{amount:'$'+dollars});
            }
            var use = BASE+'/discount/'+encodeURIComponent(c.discount_code)+'?redirect='+encodeURIComponent(BASE+'/cart');
            return '<div class="kk-reward"><div class="kk-reward__icon">✦</div><div class="kk-reward__body">'+
              '<div class="kk-reward__code">'+esc(c.discount_code)+'</div><div class="kk-reward__desc">'+desc+'</div></div>'+
              '<a class="button button--small" href="'+esc(use)+'">'+tpl('code_use_label','Use')+'</a></div>';
          }).join('');
        }).catch(function(){
          if(meta) meta.textContent = '';
          wrap.innerHTML='<div class="kk-empty kk-empty--error">'+tpl('codes_error','Couldn’t load your codes.')+' '+
            '<button type="button" class="button button--small button--secondary js-codes-retry">'+tpl('codes_retry_label','Retry')+'</button></div>';
          var btn=wrap.querySelector('.js-codes-retry'); if(btn) btn.addEventListener('click', loadCodes);
        });
      }
      loadCodes();

      api('/history').then(function(d){
        var tb=root.querySelector('.js-history'); if(!tb) return;
        var h=d.history||[];
        var meta=root.querySelector('.js-hist-meta'); if(meta) meta.textContent = tpl('hist_meta','{n} activities',{n:h.length});
        if(!h.length){ tb.innerHTML='<tr><td colspan="3" class="kk-empty">'+tpl('hist_empty','No activity yet.')+'</td></tr>'; return; }
        tb.innerHTML = h.map(function(x){
          var cls=x.delta>0?'pos':'neg'; var sign=x.delta>0?'+':'';
          var d=new Date(Number(x.date)); var date=isNaN(d)?'':d.toLocaleDateString(undefined,{month:'short',day:'numeric'});
          return '<tr><td>'+esc(date)+'</td><td>'+esc(x.label)+'</td><td class="'+cls+'">'+sign+esc(x.delta)+'</td></tr>';
        }).join('');
      }).catch(function(){});
    }

    // Reward-card icons (inline so the JS-rendered cards need no liquid). The
    // catalog has no per-reward image for money/credit rewards, so those cards
    // key off type: a gift for free product/collection, a coin for store
    // credit, a ticket for a $-off discount code, a percent mark for % off.
    var RWD_ICON_GIFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="9" width="18" height="11" rx="1"/><path d="M3 13h18M12 9v11M12 9S10 4 7.5 5 9 9 12 9Zm0 0s2-5 4.5-4-.5 4-4.5 4Z"/></svg>';
    var RWD_ICON_COIN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M14.6 9.3c-.4-.9-1.4-1.4-2.6-1.4-1.5 0-2.6.8-2.6 2 0 2.7 5.2 1.3 5.2 4 0 1.2-1.1 2-2.6 2-1.2 0-2.2-.5-2.6-1.4"/><path d="M12 6.2v1.7M12 16.1v1.7"/></svg>';
    var RWD_ICON_TICKET = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8Z"/><path d="M15 6.5v1.5M15 11v2M15 16v1.5"/></svg>';
    var RWD_ICON_PCT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>';
    var RWD_ICON_LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
    // Pick the fallback glyph for a reward that has no product/collection image.
    function rewardIcon(type){
      if(type==='free_product' || type==='free_collection' || type==='bogo') return RWD_ICON_GIFT;
      if(type==='fixed_credit' || type==='variable_credit') return RWD_ICON_COIN;
      if(type==='percent') return RWD_ICON_PCT;
      return RWD_ICON_TICKET; // fixed / variable $-off → discount code
    }
    // Display art: the real product/collection image when the catalog has one
    // (set in the admin), else a type glyph mounted on a coin-style medallion so
    // money/credit cards read as designed art rather than a missing image.
    // Pre-fetch every reward's artwork — the row thumbnail AND each variant's
    // own photo, since a chip click swaps to it. Fire-and-forget: the browser
    // caches the response, so the <img> the row renders later is an instant
    // cache hit instead of a visible pop-in. Deduped, because several rewards
    // can share one product image.
    var artWarmed = {};
    function warmRewardArt(options){
      (options || []).forEach(function(o){
        var urls = [o.image_url].concat((o.variants || []).map(function(v){ return v.image_url; }));
        urls.forEach(function(u){
          if(!u || artWarmed[u]) return;
          artWarmed[u] = 1;
          var img = new Image();
          img.decoding = 'async';
          img.src = u;
        });
      });
    }
    function rewardArt(o){
      if(o.image_url) return '<img class="kk-rwd__img kk-img-skel" src="'+esc(o.image_url)+'" alt="" loading="eager" fetchpriority="high" decoding="async">';
      return '<span class="kk-rwd__medal">'+rewardIcon(o.type)+'</span>';
    }
    // ── Reward rows ──────────────────────────────────────────────────────────
    // One uniform row per REWARD ROW (see groupRows below), hairline-separated
    // inside the redeem panel. Exactly one primary button per panel — the
    // featured row; every other redeemable row uses the secondary button.
    //
    // Row states, all driven by live /options + /balance data:
    //   affordable → Redeem button · short → "N pts to go" + progress bar ·
    //   tier-locked → the unlock line (no covering overlay: a 64px row has
    //   nowhere to put one) · guest → "Join to redeem".

    // Selected member per reward group, keyed by group_key. A group is several
    // catalogue rewards sharing a `group_key` (migration 0050) — the same
    // reward in several flavours. They collapse into ONE row whose chips switch
    // between them; each chip is still a real reward with its own id, cost and
    // affordability, so redeeming uses the SELECTED member's own reward id.
    var groupPick = {};

    /** Collapse the option list into render rows. A row is either a standalone
     *  option (`members` of one) or a whole group. Groups take the position of
     *  their cheapest member so a group sorts where its entry price says it
     *  should. */
    function groupRows(options){
      var rows = [], byKey = {};
      options.forEach(function(o){
        if(!o.group_key){ rows.push({ key:null, members:[o] }); return; }
        var row = byKey[o.group_key];
        if(!row){ row = byKey[o.group_key] = { key:o.group_key, members:[] }; rows.push(row); }
        row.members.push(o);
      });
      return rows;
    }

    /** The member a group row currently renders: the member the shopper picked,
     *  else the cheapest one they can afford, else simply the cheapest — so a
     *  group opens on something redeemable whenever one is. */
    function rowOption(row){
      if(row.members.length===1) return row.members[0];
      var picked = groupPick[row.key];
      if(picked){
        var hit = row.members.filter(function(m){ return m.id===picked; })[0];
        if(hit) return hit;
      }
      var usable = row.members.filter(function(m){ return m.affordable && !m.locked; });
      return usable[0] || row.members[0];
    }

    /** The row's heading: the group label the merchant set on any member (first
     *  one wins, matching the admin hint), else that member's own label. */
    function rowTitle(row, o){
      if(row.members.length===1) return o.label;
      var labelled = row.members.filter(function(m){ return m.group_label; })[0];
      return (labelled && labelled.group_label) || row.members[0].label;
    }

    // Selected VARIANT per reward, keyed by reward id, and the colourway picked
    // within it. A reward's chips are its own variants (one reward, one price,
    // N variants) — NOT sibling rewards. The chip's word is the variant's
    // `name`; it deliberately never falls back to the variant title, because
    // that title is the product title and would restate the row heading the
    // chip sits under ("Free Strawberry Sleeves" under "Free pack of clear
    // sleeves").
    var variantPick = {}, groupPick = {};

    /** The variant a row currently renders: the shopper's pick, else the first. */
    function rowVariant(o){
      var vs = o.variants || [];
      if(!vs.length) return null;
      var picked = variantPick[o.id];
      if(picked!=null){
        var hit = vs.filter(function(v){ return String(v.id)===String(picked); })[0];
        if(hit) return hit;
      }
      return vs[0];
    }

    /** Chip strip + sub-line for a reward with two or more variants.
     *  Below two variants there is nothing to choose, so no chip row renders at
     *  all — a lone chip just repeats the row title.
     *  The sub-line slot holds ONE thing: the selected variant's fit caption,
     *  or its colourway select when it has one. Never a restatement of the
     *  description above it. */
    /** Chip strip + sub-line for a row.
     *
     *  Chips come from whichever axis actually holds the choice, because the
     *  catalogue models the same-looking shelf two different ways:
     *
     *   - SEPARATE PRODUCTS → separate rewards, collapsed by `group_key`.
     *     Strawberry / Vanilla / Mint Choc are three products, so they can
     *     never be variants of one another; each is its own reward and the
     *     chips are the group's members. Same for Galaxy / Palette.
     *   - ONE PRODUCT, SEVERAL VARIANTS → one reward, chips are its variants.
     *     The Standloader triple pack (Standard / Photocard) is one product.
     *
     *  The two compose: a grouped row's selected MEMBER can itself have
     *  variants, and those become the second-level select (Galaxy → Moonlight /
     *  Nebula / Aurora). That is why the sub-line is computed from the selected
     *  member rather than from the row.
     *
     *  Below two choices nothing renders — a lone chip just repeats the row
     *  title.
     */
    function chipsHTML(row, o){
      var grouped = row.members.length > 1;
      var vs = o.variants || [];
      var chips = '';

      if(grouped){
        // Chips across separate rewards (separate products). The chip word is
        // the member's `short_name` (→ swatch_label), never its label, which is
        // the product title and would restate the row heading.
        chips = '<div class="kk-rwd__chips">' + row.members.map(function(m, i){
          var dot = m.swatch_color ? '<span class="kk-rwd__chip-dot" style="background:'+esc(m.swatch_color)+'"></span>' : '';
          var cost = (m.points !== o.points) ? '<em class="kk-rwd__chip-cost">'+(m.points||0).toLocaleString()+'</em>' : '';
          return '<button type="button" class="kk-rwd__chip js-rwd-chip" data-group="'+esc(row.key)+'" data-id="'+esc(m.id)+'" aria-pressed="'+(m.id===o.id?'true':'false')+'">'+
            dot + esc(m.swatch_label || tpl('rwd_variant_fallback','Option {n}',{n:i+1})) + cost +
          '</button>';
        }).join('') + '</div>';
      } else if(vs.length >= 2){
        // Chips across the variants of ONE product.
        var selv = rowVariant(o);
        chips = '<div class="kk-rwd__chips">' + vs.map(function(v, i){
          var dot = v.swatch ? '<span class="kk-rwd__chip-dot" style="background:'+esc(v.swatch)+'"></span>' : '';
          return '<button type="button" class="kk-rwd__chip js-rwd-chip" data-reward="'+esc(o.id)+'" data-variant="'+esc(String(v.id))+'" aria-pressed="'+(selv && String(v.id)===String(selv.id)?'true':'false')+'">'+
            dot + esc(v.name || tpl('rwd_variant_fallback','Option {n}',{n:i+1})) +
          '</button>';
        }).join('') + '</div>';
      }

      // Sub-line — ONE thing, never a restatement of the description above it.
      // A grouped member with several variants offers them as the second-level
      // select; anything else shows the selected variant's fit caption.
      var sub = '';
      var sel = rowVariant(o);
      if(grouped && vs.length >= 2){
        var id = 'kk-rwd-way-' + esc(String(o.id));
        var cap = tpl('rwd_colourway_label','colour');
        sub = '<div class="kk-rwd__sub">'+
          '<span class="kk-rwd__sub-cap">'+cap+'</span>'+
          // The sitewide select (assets/kkumi-input.css), in its `--chip` size:
          // same surface, border, radius, caret and focus ring as the account
          // address pickers, just sized to sit on a line of content with the
          // label outside it. Not a bespoke control.
          '<span class="kkumi-input kkumi-input--select kkumi-input--chip">'+
            '<select id="'+id+'" class="kkumi-input__field js-rwd-colourway" data-reward="'+esc(o.id)+'" aria-label="'+esc((o.swatch_label||'')+' '+cap)+'">'+
              vs.map(function(v, i){
                var nm = v.name || v.title || tpl('rwd_variant_fallback','Option {n}',{n:i+1});
                return '<option value="'+esc(String(v.id))+'"'+(sel && String(v.id)===String(sel.id)?' selected':'')+'>'+esc(nm)+'</option>';
              }).join('')+
            '</select>'+
          '</span>'+
        '</div>';
      } else if(sel && sel.caption){
        sub = '<div class="kk-rwd__sub"><span class="kk-rwd__sub-cap">'+esc(sel.caption)+'</span></div>';
      }
      return chips + sub;
    }

    /** Type-derived blurb for a reward with no merchant-authored description. */
    function rewardDesc(o){
      var isFree = o.type==='free_product' || o.type==='free_collection';
      if(o.type==='free_collection') return o.product_title?tpl('rwd_desc_free_collection','One free from {title}.',{title:esc(o.product_title)}):tpl('rwd_desc_free_collection_fallback','A free pick on us.');
      if(isFree) return o.product_title?tpl('rwd_desc_free_product','Free {title}.',{title:esc(o.product_title)}):tpl('rwd_desc_free_product_fallback','A free product on us.');
      if(o.type==='percent'){
        var pct = (o.percent_off||0)+'%';
        if(o.percent_scope==='collection') return tpl('rwd_desc_percent_collection','{pct} off {title}.',{pct:pct,title:esc(o.product_title||tpl('rwd_percent_collection_fallback','a select collection'))});
        if(o.percent_scope==='product') return tpl('rwd_desc_percent_collection','{pct} off {title}.',{pct:pct,title:esc(o.product_title||tpl('rwd_percent_product_fallback','a select product'))});
        return tpl('rwd_desc_percent_order','{pct} off your whole order.',{pct:pct});
      }
      if(o.type==='bogo'){
        if(o.bogo_mode==='product') return tpl('rwd_desc_bogo_product','Buy {title}, get {gift} free.',{title:esc(o.product_title||tpl('rwd_bogo_product_fallback','one')),gift:esc(o.gift_product_title||tpl('rwd_bogo_gift_fallback','a gift'))});
        if(o.bogo_mode==='collection_half') return tpl('rwd_desc_bogo_half','Buy 1 + get 1 50% off from {title}.',{title:esc(o.product_title||tpl('rwd_bogo_collection_fallback','a collection'))});
        return tpl('rwd_desc_bogo_free','Buy 1 + get 1 free from {title}.',{title:esc(o.product_title||tpl('rwd_bogo_collection_fallback','a collection'))});
      }
      if(o.type==='fixed_credit') return tpl('rwd_desc_fixed_credit','{amount} added to your Shopify store credit; tick "Store credit" at checkout to apply.',{amount:'$'+((o.discount_cents||0)/100).toFixed(0)});
      if(o.type==='variable_credit') return tpl('rwd_desc_variable_credit','Turn points into Shopify store credit; tick "Store credit" at checkout to apply.');
      if(o.type==='fixed') return tpl('rwd_desc_fixed','{amount} off your next order, delivered as a discount code.',{amount:'$'+((o.discount_cents||0)/100).toFixed(0)});
      return tpl('rwd_desc_default','Money off your next order.');
    }

    function rewardRow(row, isFeature){
      var o = rowOption(row);
      var pts = o.points||0;
      var locked = !!o.locked, affordable = !!o.affordable;
      var tier = esc(o.min_tier_name||tpl('rwd_min_tier_fallback','next tier'));
      // Per-tier icon from the tiers section (SVG markup, already in the DOM).
      // Falls back to the ✦ glyph when the tiers section isn't on this template.
      var tierIc = tierIconHtml(o.min_tier);
      // Merchant-authored description (admin reward editor) wins over the
      // type-derived fallback. API-controlled → escape per FRONTEND.md. It
      // always stays on the row's own description line; the sub-line under the
      // chips is for the fit caption / colourway select ONLY, never a second
      // rendering of this sentence.
      var desc = o.description ? esc(o.description) : rewardDesc(o);

      var ribbon = '';
      if(isFeature) ribbon='<span class="kk-rwd__ribbon">'+tpl('rwd_ribbon_most_wanted','✦ most wanted')+'</span>';
      else if(o.exclusive && !locked){
        // Top tier → "Idol only" (nothing above it); otherwise "Rookie+" (that
        // tier and every tier above can redeem). Leading glyph = the tier icon.
        var exclLabel = isTopTier(o.min_tier) ? tpl('rwd_ribbon_only','{tier} only',{tier:tier}) : tpl('rwd_ribbon_plus','{tier}+',{tier:tier});
        ribbon = '<span class="kk-rwd__ribbon kk-rwd__ribbon--excl"><span class="kk-rwd__ribbon-ic">'+tierIc+'</span>'+exclLabel+'</span>';
      }

      // Action column. Every state is always visible — no hover-only content.
      var act;
      if(locked){
        // Tier gate: no button and no progress bar — points-to-go is meaningless
        // when what blocks you is the tier, not the balance. The cost still
        // shows above it.
        act = '<span class="kk-rwd__lock">'+(canUse()
          ? tpl('rwd_lock_tier','reach <b>{tier}</b> to unlock',{tier:tier})
          : tpl('rwd_lock_guest_sub','join & climb to unlock'))+'</span>';
      } else if(!canUse()){
        act = '<a class="button button--small button--secondary" href="'+esc(joinHref())+'">'+tpl('rwd_join_label','Join to redeem')+'</a>';
      } else if(affordable){
        act = '<button type="button" class="button button--small'+(isFeature?'':' button--secondary')+' js-redeem" data-id="'+esc(o.id)+'" data-points="'+pts+'">'+tpl('rwd_redeem_label','Redeem')+'</button>';
      } else if(lastBalance!=null && pts>0){
        // Out of reach: how far, plus the bar. Replaces the old disabled button
        // — a greyed-out primary read as a broken control.
        var bal = lastBalance;
        var pct = Math.max(2, Math.min(100, Math.round(bal/pts*100)));
        var togo = Math.max(0, pts-bal);
        act = '<span class="kk-rwd__togo">'+tpl('rwd_togo','<b>{n} pts</b> to go',{n:togo.toLocaleString()})+'</span>'+
              '<span class="kk-rwd__bar"><span style="width:'+pct+'%"></span></span>';
      } else {
        // Balance not loaded yet — the bar would render at a lie, so fall back
        // to the disabled button until /balance lands and we re-render.
        act = '<button type="button" class="button button--small button--secondary" disabled>'+tpl('rwd_keep_earning_label','Keep earning')+'</button>';
      }

      // `--pick` marks a row carrying the colourway select, which needs the
      // action column on its own line at any width (see the CSS note).
      var sel = rowVariant(o);
      // The row's art follows the selected variant when that variant has its
      // own photo; otherwise the reward's image, else the type glyph. Chosen at
      // render time — the row re-renders on every chip click, so there is
      // nothing to swap afterwards.
      var art = (sel && sel.image_url) ? { image_url: sel.image_url } : o;

      // `--dim` = "you cannot redeem this right now", whether the blocker is the
      // tier or the balance. Thumbnail + title drop back; never a greyed-out
      // pink button, which is what made the original block shout.
      var dim = locked || (canUse() && !affordable);
      return '<article class="kk-rwd'+(isFeature?' kk-rwd--feature':'')+(dim?' is-locked':'')+'">'+
        '<div class="kk-rwd__display'+(art.image_url?' has-img':'')+'">'+rewardArt(art)+'</div>'+
        '<div class="kk-rwd__body">'+ribbon+
          '<p class="kk-rwd__t">'+esc(rowTitle(row,o))+'</p>'+
          '<p class="kk-rwd__d">'+desc+'</p>'+
          chipsHTML(row,o)+
        '</div>'+
        '<div class="kk-rwd__act"><span class="kk-rwd__cost">'+pts.toLocaleString()+'<small>'+tpl('rwd_pts_unit_short','pts')+'</small></span>'+act+'</div>'+
        '</article>';
    }
    function loadOptions(){
      // Shared /options fetch — populates the redeem grid + money-off stepper
      // (if present) AND drives the hero membercard "next reward at X pts"
      // progress widget. Called once even if the redeem section isn't on the
      // page so the progress widget still works.
      return api('/options').then(function(d){
        lastOptions = d.options || [];
        warmRewardArt(lastOptions);
        renderProgress(); renderMoneyOff(); renderGrid(); renderShelf();
        if(window.__kkWalletLoad) window.__kkWalletLoad.done('options');
      }).catch(function(){ if(window.__kkWalletLoad) window.__kkWalletLoad.done('options'); renderShelf(true); var grid=root.querySelector('.js-redeem-grid'); if(grid){ grid.innerHTML='<div class="kk-empty">'+tpl('grid_error','Couldn’t load rewards.')+'</div>'; grid.removeAttribute('aria-busy'); } });
    }
    // ── Rewards shelf (kkumi-club-redeem layout "shelf", v11 Club page) ──────
    // A read-only showcase of what points buy: one tile per reward anyone can
    // claim (no tier gate), cheapest first, one tile per group. Tile copy
    // (units, sub-labels) rides data-c-* on the shelf; reward names and image
    // URLs come from /options and are escaped. Redeeming happens in the
    // account's Rewards tab, which the shelf links to.
    function renderShelf(failed){
      root.querySelectorAll('.js-redeem-shelf').forEach(function(shelf){
        var d=shelf.dataset;
        function sc(k, fb){ var v=d[k]; return v!=null&&v!=='' ? v : fb; }
        if(failed){ shelf.innerHTML='<div class="kk-empty">'+esc(tpl('grid_error','Couldn’t load rewards.'))+'</div>'; shelf.removeAttribute('aria-busy'); return; }
        if(!lastOptions) return;
        var max=parseInt(d.max,10)||4, seen={}, tiles=[];
        lastOptions.filter(function(o){ return !o.locked && !o.exclusive && o.type!=='variable'; })
          .map(function(o){ return { o:o, cost:(o.type==='variable_credit' ? Number(o.step_points) : Number(o.points))||0 }; })
          .sort(function(a,b){ return a.cost-b.cost; })
          .forEach(function(x){
            if(x.o.group_key){ if(seen[x.o.group_key]) return; seen[x.o.group_key]=1; }
            // A "$5 store credit" fixed reward and the choose-your-amount
            // credit at the same step read as the same tile: show it once.
            var credit=(x.o.type==='variable_credit' ? x.o.step_discount_cents : (x.o.type==='fixed_credit' ? x.o.discount_cents : null));
            if(credit!=null){ var ck='credit:'+credit+':'+x.cost; if(seen[ck]) return; seen[ck]=1; }
            if(tiles.length<max) tiles.push(x);
          });
        if(!tiles.length){ shelf.innerHTML='<div class="kk-empty">'+esc(tpl('grid_empty','No rewards available yet.'))+'</div>'; shelf.removeAttribute('aria-busy'); return; }
        function money(c){ return window.KKMoney ? window.KKMoney.fromCents(c,{shop:true,trim:true}) : String(Math.round(c/100)); }
        shelf.innerHTML = tiles.map(function(x){
          var o=x.o, art, name=o.group_label || o.label || '';
          if(o.type==='variable_credit'){
            art='<div class="kk-shelf__flat"><b>'+esc(money(o.step_discount_cents||0))+'</b><span>'+esc(sc('cCreditSub','store credit'))+'</span></div>';
            name=sc('cCreditName','[amount] store credit').replace(/\[amount\]|\{amount\}/g, money(o.step_discount_cents||0));
          } else if(o.image_url){
            art='<div class="kk-shelf__media"><img class="kk-img-skel" src="'+esc(o.image_url)+'" alt="" loading="lazy" decoding="async"></div>';
          } else if(o.type==='fixed_credit' || o.type==='fixed'){
            art='<div class="kk-shelf__flat"><b>'+esc(money(o.discount_cents||0))+'</b><span>'+esc(o.type==='fixed_credit' ? sc('cCreditSub','store credit') : sc('cOffSub','off'))+'</span></div>';
          } else if(o.type==='percent' && o.percent_off){
            art='<div class="kk-shelf__flat"><b>'+esc(o.percent_off+'%')+'</b><span>'+esc(sc('cOffSub','off'))+'</span></div>';
          } else {
            art='<div class="kk-shelf__flat kk-shelf__flat--icon">'+rewardIcon(o.type)+'</div>';
          }
          return '<div class="kk-shelf__tile">'+art+'<div class="kk-shelf__body"><h3 class="kk-shelf__name">'+esc(name)+'</h3>'+
            '<p class="kk-shelf__cost"><b>'+esc(fmt(x.cost))+'</b><small>'+esc(sc('cPtsUnit','pts'))+'</small></p></div></div>';
        }).join('');
        shelf.removeAttribute('aria-busy');
      });
    }

    // Rows per page. The panel is a vertical list now, so a page is a run of
    // rows rather than a grid of cards; the pager sits inside the panel, under
    // the rows and above the footnote.
    var ROWS_PER_PAGE = 4;

    // Build the reward rows from cached /options + the current balance.
    // Re-run when /balance lands too: the /options and /balance fetches race and
    // either can resolve first — without a re-render every row's affordability
    // and progress bar is computed against a null balance at first paint.
    function renderGrid(){
      if(!lastOptions) return;
      var grid=root.querySelector('.js-redeem-grid');
      if(!grid) return;
      // Variable rewards drive the points→credit converter (renderMoneyOff),
      // not the row list — the list is fixed-cost + free-product rewards,
      // cheapest → most expensive (catalog `position` breaks ties).
      var cards = lastOptions.filter(function(o){ return o.type!=='variable' && o.type!=='variable_credit'; });
      if(!cards.length){ grid.innerHTML='<div class="kk-empty">'+tpl('grid_empty','No rewards available yet.')+'</div>'; grid.removeAttribute('aria-busy'); renderRedeemCount(0,0); return; }
      var ordered = cards.slice().sort(function(a, b){
        var ap = a.points != null ? a.points : Number.MAX_SAFE_INTEGER;
        var bp = b.points != null ? b.points : Number.MAX_SAFE_INTEGER;
        if (ap !== bp) return ap - bp;
        return (a.position || 0) - (b.position || 0);
      });
      // Collapse reward groups into single rows AFTER the sort, so a group
      // lands at its cheapest member's position and its chips stay in
      // cheapest-first order.
      var rows = groupRows(ordered);
      // Feature = the row holding the server-flagged most-redeemed reward
      // (falls back to the first row the member can actually redeem, then the
      // first row). It is the ONE primary button in the panel.
      var featureRow = rows.filter(function(r){ return r.members.some(function(m){ return m.featured && !m.locked; }); })[0]
        || rows.filter(function(r){ var o=rowOption(r); return o.affordable && !o.locked; })[0]
        || rows[0];
      // Hoist the feature to the top of the list. The rest stays cheapest-first,
      // but the feature cannot be left in price order: it carries the panel's
      // ONE primary button, so a dear feature would sort onto page 2 and leave
      // page 1 with no primary button and the hero hidden behind a pager click.
      // (The old card grid solved this by repeating the feature as a fixed
      // bookend on every page; a uniform row list hoists it once instead.)
      if(featureRow){
        var fi = rows.indexOf(featureRow);
        if(fi > 0){ rows.splice(fi, 1); rows.unshift(featureRow); }
      }

      var pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
      // A chip click re-renders the whole list, so carry the reader's page over
      // — otherwise picking a flavour on page 2 snapped them back to page 1.
      var active = Math.min(parseInt(grid.getAttribute('data-kk-active-page'),10)||0, pageCount-1);
      function rowsHTML(slice){ return slice.map(function(r){ return rewardRow(r, r===featureRow); }).join(''); }
      if(pageCount <= 1){
        grid.className = 'kk-rdm__list js-redeem-grid';
        grid.removeAttribute('data-kk-active-page');
        grid.innerHTML = rowsHTML(rows);
      } else {
        grid.className = 'kk-rdm__pages js-redeem-grid';
        grid.setAttribute('data-kk-active-page', String(active));
        var ph = '';
        for(var p=0;p<pageCount;p++){
          ph += '<div class="kk-rdm__page kk-rdm__list" data-kk-page="'+p+'"'+(p===active?'':' hidden')+'>' + rowsHTML(rows.slice(p*ROWS_PER_PAGE, p*ROWS_PER_PAGE+ROWS_PER_PAGE)) + '</div>';
        }
        ph += window.KKPager ? '<div class="kk-rdm__pager">'+window.KKPager.navHTML(pageCount, active)+'</div>' : '';
        grid.innerHTML = ph;
      }
      grid.removeAttribute('aria-busy');
      grid.querySelectorAll('.js-redeem').forEach(function(b){ b.addEventListener('click', function(){ redeem(b, Number(b.dataset.points)); }); });
      // Chip click → remember the pick for that group and re-render, so the
      // row's cost, sub-line and affordability state all move together (a
      // dearer flavour can flip a redeemable row to "N pts to go").
      // Chip click → remember the variant for that reward and re-render, so the
      // sub-line (fit caption / colourway select) and any per-variant cost move
      // with it.
      grid.querySelectorAll('.js-rwd-chip').forEach(function(c){
        c.addEventListener('click', function(){
          var d = c.dataset;
          if(d.group){ groupPick[d.group] = d.id; }   // switch member (product)
          else { variantPick[d.reward] = d.variant; } // switch variant
          renderGrid();
          // The re-render replaces the chip node, which would drop a keyboard
          // user's focus to <body>. Put it back on the chip they just chose.
          // Matched in JS, not by attribute selector: a reward id can be
          // merchant-supplied, so it isn't guaranteed selector-safe.
          grid.querySelectorAll('.js-rwd-chip').forEach(function(n){
            if(d.group ? (n.dataset.group===d.group && n.dataset.id===d.id)
                       : (n.dataset.reward===d.reward && n.dataset.variant===d.variant)) n.focus();
          });
        });
      });
      // Colourway select → remembered per reward, and read at redeem time.
      // No re-render: nothing else on the row depends on it.
      // The second-level select picks a VARIANT of the rendered reward, so it
      // writes the same state the variant chips do. Re-render: the row's art
      // and caption follow the variant.
      grid.querySelectorAll('.js-rwd-colourway').forEach(function(sel){
        sel.addEventListener('change', function(){
          variantPick[sel.dataset.reward] = sel.value;
          renderGrid();
        });
      });
      if(pageCount > 1 && window.KKPager){ window.KKPager.mount(grid); }
      renderRedeemCount(rows.length, rows.filter(function(r){ var o=rowOption(r); return o.affordable && !o.locked; }).length);
      // The wallet's worth line quotes the in-reach count, which only exists
      // once the rows are built — so refresh it here rather than leaving it a
      // render behind. (renderWallet never calls back into renderGrid.)
      renderWallet();
    }

    // "N rewards · X in reach with Y pts" — the cart panel's header meta, and
    // the source of the in-reach half of the club wallet's worth line. Counts
    // ROWS, not catalogue entries, so a four-flavour group counts once.
    var lastRowCount = 0, lastInReach = 0;
    function renderRedeemCount(total, inReach){
      lastRowCount = total; lastInReach = inReach;
      var el = root.querySelector('.js-redeem-count');
      if(!el) return;
      if(!canUse() || lastBalance==null){
        el.textContent = tpl('redeem_count_guest','{n} rewards',{n:total.toLocaleString()});
        return;
      }
      el.innerHTML = tpl('redeem_count','{n} rewards · <b>{r} in reach</b> with {bal} pts',{
        n: total.toLocaleString(), r: inReach.toLocaleString(), bal: lastBalance.toLocaleString(),
      });
    }

    // Wallet strip worth line — "worth up to $X off · N of M rewards in reach".
    // Recomputed when the balance (/balance), the live rate (/program), or the
    // row list (renderGrid) changes; the counts come from renderRedeemCount and
    // are row counts, so a grouped reward counts once.
    function renderWallet(){
      if(lastBalance==null) return;
      var step=REDEEM_STEP||100, dollars=REDEEM_DOLLARS||5;
      var worth=Math.floor(lastBalance/step)*dollars;
      var reach = lastRowCount
        ? tpl('wallet_reach',' · {r} of {n} rewards in reach',{r:lastInReach.toLocaleString(),n:lastRowCount.toLocaleString()})
        : '';
      setAll('.js-redeem-worth', worth>0
        ? tpl('wallet_worth','worth up to {amount} off',{amount:'$'+worth}) + reach
        : tpl('wallet_earn_first','earn {n} pts to redeem your first reward',{n:step.toLocaleString()}));
    }

    // Money-off stepper — the storefront face of the "variable" reward. Steps
    // by the live redeem step; max is what the member can currently afford
    // (server-computed in /options). Apply runs the normal redeem() flow.
    var moId=null, moStep=100, moDollars=5, moMax=0, moCur=0;
    // Stepper rendered for whichever variable-class reward is active: the
    // discount-code "$ off" variant OR the store-credit variant. The merchant
    // configures one (enabling both makes the credit one win).
    var moIsCredit = false;
    function moUpd(){
      var block=root.querySelector('.js-moneyoff'); if(!block) return;
      var dollars=Math.round((moCur/moStep)*moDollars);
      var v=block.querySelector('.js-mo-val'); if(v) v.textContent = moIsCredit ? tpl('mo_val_credit','{amount} credit',{amount:'$'+dollars}) : tpl('mo_val_off','{amount} off',{amount:'$'+dollars});
      var p=block.querySelector('.js-mo-pts'); if(p) p.textContent=tpl('mo_pts_unit','costs {n} pts',{n:moCur.toLocaleString()});
      var down=block.querySelector('.js-mo-down'); if(down) down.disabled = moCur<=moStep;
      var up=block.querySelector('.js-mo-up'); if(up) up.disabled = moCur>=moMax;
      var apply=block.querySelector('.js-mo-apply'); if(apply){ apply.disabled = moMax<moStep; apply.dataset.id=moId||''; }
      // "Convert 1,000 points to $5 credit" — the trade stated in full, in the
      // shopper's own numbers, before they press anything. The template is a
      // merchant setting on the host block (`data-kk-explain`, cart wallet);
      // no attribute → no line, so hosts that don't want one opt out by
      // leaving the setting blank.
      var ex=block.querySelector('.js-mo-explain');
      if(ex){
        var exTpl=block.dataset.kkExplain||'';
        ex.textContent = exTpl
          ? exTpl.split('{n}').join(moCur.toLocaleString())
                 .split('{amount}').join('$'+dollars)
          : '';
      }
    }
    function renderMoneyOff(){
      var block=root.querySelector('.js-moneyoff'); if(!block) return;
      // Prefer variable_credit if both are configured (the merchant most
      // likely switched to credit and forgot to disable the code variant).
      var v=null;
      (lastOptions||[]).forEach(function(o){ if(o.type==='variable_credit') v=o; });
      if(!v) (lastOptions||[]).forEach(function(o){ if(o.type==='variable') v=o; });
      if(!v || v.locked || !canUse()){ block.setAttribute('hidden',''); return; }
      moIsCredit = v.type === 'variable_credit';
      moId=v.id; moStep=v.step_points||REDEEM_STEP||100; moDollars=REDEEM_DOLLARS||5; moMax=v.max_points||0;
      moCur=Math.min(Math.max(moStep, moCur||moStep), Math.max(moStep, moMax));
      block.removeAttribute('hidden');
      moUpd();
      renderRedeemBal();
    }
    // Redeem subtext ("You have N points to redeem") — when the member can't yet
    // afford the smallest redemption (balance < one step), flip it to tell them
    // how far off they are. moStep is the live minimum from /options; the "have"
    // copy (merchant-set) is stashed so we can restore it.
    function renderRedeemBal(){
      var line=root.querySelector('.kkumi-wallet__redeem-bal'); if(!line) return;
      var bal=(lastBalanceObj && lastBalanceObj.balance)||0;
      var min=moStep||0;
      if(min && bal<min){
        // Can't afford the smallest redemption: say how far off they are. This
        // is the ONLY state the line earns its space in — hence the unhide.
        if(line.dataset.kkHave==null) line.dataset.kkHave=line.innerHTML;
        line.classList.add('is-away');
        line.innerHTML=tpl('redeem_away','You’re <b>{n}</b> points away from store credit',{n:(min-bal).toLocaleString()});
        line.removeAttribute('hidden');
      } else {
        // They can redeem, so "You have N points to redeem" would only restate
        // the balance rendered directly above it. Keep it hidden and let the
        // panel be shorter. (Restore the markup first so a later flip back to
        // the away state still has its original template to fall back on.)
        if(line.dataset.kkHave!=null){ line.innerHTML=line.dataset.kkHave; delete line.dataset.kkHave; line.classList.remove('is-away'); }
        line.setAttribute('hidden','');
        setAll('.js-redeem-bal', bal.toLocaleString());
      }
    }

    // POST to a storefront cart AJAX endpoint, rejecting on a refusal.
    //
    // Shopify answers a rejected /cart/add.js with an HTTP error (422 when the
    // variant is sold out, unavailable in this market, or over its quantity
    // rule; 404 when it no longer exists) and a JSON body carrying a
    // `description`. fetch() resolves for all of those, so a chain that only
    // looks at `.then` treats a refusal as a success: the old redeem flow went
    // on to attach the discount and redirect to /cart announcing a reward that
    // was never added. The shopper landed on an EMPTY cart holding an unused
    // code, with nothing on screen saying why. Reject instead, so the caller's
    // existing fallback (hand over the code, keep the member on the page) runs.
    function cartPost(path, body){
      return fetch(BASE+path, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
        credentials:'same-origin',
        body: JSON.stringify(body),
      }).then(function(r){
        return r.json().catch(function(){ return null; }).then(function(j){
          if(!r.ok){
            var e = new Error((j && (j.description || j.message)) || ('HTTP '+r.status));
            e.cartError = (j && j.description) || null;
            throw e;
          }
          return j;
        });
      });
    }

    // Every variant /cart/add.js reports back, as strings. A 200 with none of
    // the ids we asked for means nothing landed — treat that as a refusal too
    // rather than redirecting to a cart that doesn't hold the reward.
    function addedVariantIds(res){
      var list = (res && res.items) ? res.items : (res && res.variant_id != null ? [res] : []);
      return list.map(function(i){ return String(i.variant_id != null ? i.variant_id : i.id); });
    }

    // Tell every other rewards surface on the page that the balance moved.
    // The club sections re-read /balance themselves after a redemption, but the
    // launcher FAB (assets/kkumi-club-launcher.js) holds its own copy and only
    // refreshed on page load or tab focus — so its points pill kept the
    // pre-redemption number until the shopper reloaded. Listeners refetch; they
    // must never re-dispatch, or two surfaces would ping-pong forever.
    function announcePointsChanged(){
      try { document.dispatchEvent(new CustomEvent('kkumi:points-changed', { detail:{ source:'club' } })); } catch(e) {}
    }

    // Items the storefront should auto-add to cart for a successful redeem.
    // - free_product / percent (product-scope): one of the chosen variant. When
    //   the reward covers several variants, `pickedId` is the member's choice
    //   from the card — honoured only if the minted code actually covers it
    //   (`variant_ids` is the server's list; the page never widens it).
    // - bogo product: the trigger AND the gift variant, so the BxGy code
    //   activates immediately (customer pays for the trigger, gets the gift
    //   free). For collection-wide BOGO modes the customer has to pick which
    //   items to buy, so we don't auto-add anything.
    function itemsToAdd(res, pickedId){
      var t = res && res.type, items = [];
      if((t==='free_product' || t==='percent') && res.variant_id){
        var covered = (res.variant_ids||[]).indexOf(Number(pickedId)) !== -1;
        items.push({ id: covered ? Number(pickedId) : Number(res.variant_id), quantity: 1 });
      } else if(t==='bogo' && res.variant_id && res.gift_variant_id){
        items.push({ id: Number(res.variant_id), quantity: 1 });
        items.push({ id: Number(res.gift_variant_id), quantity: 1 });
      }
      return items;
    }

    function redeem(btn, points){
      if(btn.disabled) return;
      // Read the variant choice now — the card is re-rendered by loadOptions()
      // while the redeem round-trip is in flight.
      var card = btn.closest ? btn.closest('.kk-rwd') : null;
      // The member's variant choice now lives in `variantPick` (set by the chip
      // row), not in a <select> on the card. Read it at click time — the list
      // re-renders mid-flight — and fall back to the row's first variant.
      var pickedRaw = variantPick[btn.dataset.id];
      var pickedId = pickedRaw!=null ? Number(pickedRaw) : null;
      // Universal busy state (disabled + aria-busy + real inline spinner).
      window.KKBtn.busy(btn, CC.redeeming_heading || 'Redeeming…');
      // Pop the cart-toast immediately so the customer sees an in-viewport
      // "Redeeming…" state the moment they click — the API round-trip plus
      // cart/add + cart/update can run several seconds.
      redemptionToast({ heading: CC.redeeming_heading || 'Redeeming…', body: CC.redeeming_body || 'Hang tight, applying your reward.', busy: true });
      api('/redeem', { method:'POST', body:{ reward_id:btn.dataset.id, points:Number(points), request_id:uuid() } })
        .then(function(res){
          if(res && (res.type==='fixed_credit' || res.type==='variable_credit')){
            window.KKBtn.reset(btn);
            // Store-credit payouts have no code — show the dollar amount that
            // landed in their account. Shopify doesn't auto-apply credit;
            // the customer ticks "Store credit" at checkout's payment step.
            var dollars = '$' + ((res.discount_cents||0)/100).toFixed(0);
            // Tell the cart's store-credit note (assets/kkumi-cart-store-credit.js)
            // to update in place — it renders optimistically off this number and
            // then reconciles with /store-credit, so the shopper never has to
            // reload the cart to see the new balance (or see the note at all,
            // when this was their first credit).
            announceCredit(res.discount_cents||0);
            if(!redemptionToast({ heading: CC.redeemed_heading || 'Redeemed ✓', body: (CC.credit_body || '{amount} added to your store credit; tick it at checkout.').replace('{amount}', dollars) })) {
              toast(tpl('toast_credit_fallback','✓ Redeemed! {amount} added to your store credit; tick it at checkout.',{amount:'<b>'+dollars+'</b>'}));
            }
          } else if (res && itemsToAdd(res, pickedId).length) {
            // For reward types that target specific variant(s) — free_product,
            // percent scope=product, bogo product (trigger + gift) — auto-add
            // them to the cart and attach the discount, then drop the
            // customer at /cart with everything done. Saves them hunting
            // for the product page and applying the code by hand. Stay busy,
            // just swap the label — we redirect to /cart on success.
            window.KKBtn.relabel(btn, CC.adding_heading || 'Adding to cart…');
            redemptionToast({ heading: CC.adding_heading || 'Adding to cart…', body: CC.adding_body || 'Almost there, taking you to your cart.', busy: true });
            var wanted = itemsToAdd(res, pickedId);
            cartPost('/cart/add.js', { items: wanted }).then(function(added){
              // A 200 that didn't actually take the variant is still a failure.
              var got = addedVariantIds(added);
              var missing = wanted.filter(function(w){ return got.indexOf(String(w.id)) === -1; });
              if(missing.length) throw new Error('cart_add_incomplete');
              return cartPost('/cart/update.js', { discount: res.discount_code });
            }).then(function(){
              window.location.href = BASE + '/cart';
            }).catch(function(err){
              // The reward is already minted and the code is valid, so never
              // lose it: hand it over and say plainly that the item did not
              // make it into the cart, instead of redirecting to a cart the
              // shopper would find empty.
              if(window.console && console.warn){
                console.warn('[kkumi] reward redeemed but not added to cart', {
                  variants: wanted.map(function(w){ return w.id; }),
                  code: res.discount_code,
                  reason: (err && (err.cartError || err.message)) || 'unknown',
                });
              }
              window.KKBtn.reset(btn);
              var use = BASE+'/discount/'+encodeURIComponent(res.discount_code)+'?redirect='+encodeURIComponent(BASE+'/cart');
              var failBody = CC.add_failed_body || 'We couldn’t add it to your cart — use this code at checkout instead:';
              if(!redemptionToast({ heading: CC.redeemed_heading || 'Redeemed ✓', body: failBody, code: res.discount_code, action: { href: use, label: CC.code_action || 'Use it now →' } })) {
                toast(tpl('toast_add_failed_fallback','✓ Redeemed! We couldn’t add it to your cart — code {code} {action}',{code:'<b>'+esc(res.discount_code)+'</b>',action:'<a href="'+esc(use)+'">'+codeActionHTML()+'</a>'}));
              }
              loadMember(); loadOptions(); announcePointsChanged();
            });
            return;
          } else {
            window.KKBtn.reset(btn);
            var use = BASE+'/discount/'+encodeURIComponent(res.discount_code)+'?redirect='+encodeURIComponent(BASE+'/cart');
            if(!redemptionToast({ heading: CC.redeemed_heading || 'Redeemed ✓', body: CC.code_body || 'Use this code at checkout:', code: res.discount_code, action: { href: use, label: CC.code_action || 'Use it now →' } })) {
              toast(tpl('toast_code_fallback','✓ Redeemed! Code {code} {action}',{code:'<b>'+esc(res.discount_code)+'</b>',action:'<a href="'+esc(use)+'">'+codeActionHTML()+'</a>'}));
            }
          }
          loadMember(); loadOptions(); announcePointsChanged();
        })
        .catch(function(e){
          window.KKBtn.reset(btn);
          // Errors go back to the page-bottom kk-toast — the cart-toast popup
          // is reserved for positive cart-related confirmations.
          hideRedemptionToast();
          toast(e.code==='insufficient_points'?(CC.error_insufficient || 'You don’t have enough points for that yet.')
            : e.code==='club_required'?(CC.error_club_required || 'Join the Kkumi Club to redeem your points.')
            : (CC.error_generic || 'Sorry, that didn’t work. Please try again.'));
        });
    }

    var moBlock=root.querySelector('.js-moneyoff');
    if(moBlock){
      var moDownBtn=moBlock.querySelector('.js-mo-down'), moUpBtn=moBlock.querySelector('.js-mo-up'), moApplyBtn=moBlock.querySelector('.js-mo-apply');
      if(moDownBtn) moDownBtn.addEventListener('click', function(){ if(moCur>moStep){ moCur-=moStep; moUpd(); } });
      if(moUpBtn) moUpBtn.addEventListener('click', function(){ if(moCur<moMax){ moCur+=moStep; moUpd(); } });
      if(moApplyBtn) moApplyBtn.addEventListener('click', function(){ if(moApplyBtn.disabled) return; redeem(moApplyBtn, moCur); });
    }

    var refBtn=root.querySelector('.js-referral-btn');
    if(refBtn){
      refBtn.addEventListener('click', function(){
        if(!canUse()){ window.location.href=joinHref(); return; }
        if(refBtn.disabled) return;
        window.KKBtn.busy(refBtn, 'Copying…');
        api('/referral').then(function(d){
          if(d.referral_url && navigator.clipboard){ navigator.clipboard.writeText(d.referral_url).catch(function(){}); }
          toast('Your referral link: <b>'+esc(d.referral_url||d.referral_code||'')+'</b> (copied!)');
        }).catch(function(){ toast('Couldn’t fetch your link. Please try again.'); })
          .finally(function(){ window.KKBtn.reset(refBtn); });
      });
    }
    function loadReferral(){
      var linkEl=root.querySelector('.js-ref-link'); if(!linkEl) return;
      api('/referral').then(function(d){
        linkEl.value=d.referral_url||'';
        set('.js-ref-code', d.referral_code||'—');
      }).catch(function(){ linkEl.value=linkEl.dataset.loadError||'Couldn’t load your link. Refresh to retry.'; });
    }
    var refCopy=root.querySelector('.js-ref-copy');
    if(refCopy){
      refCopy.addEventListener('click', function(){
        var l=root.querySelector('.js-ref-link'); if(!l||!l.value) return;
        l.select(); l.setSelectionRange(0, 99999);
        if(navigator.clipboard){ navigator.clipboard.writeText(l.value).catch(function(){}); }
        else { try{ document.execCommand('copy'); }catch(e){} }
        // Preserve the button's markup (icon + label) and just flash the
        // confirmation, so the icon/label (which come from section settings)
        // are restored intact.
        var orig=refCopy.innerHTML;
        refCopy.textContent=refCopy.dataset.copied||'Copied!';
        setTimeout(function(){ refCopy.innerHTML=orig; }, 1500);
      });
    }
    // "Got a friend's code?" — the referee-side manual entry (the share-link
    // ?ref capture above is the automatic path). One-shot server-side.
    var attachBtn=root.querySelector('.js-ref-attach-btn');
    if(attachBtn){
      attachBtn.addEventListener('click', function(){
        var input=root.querySelector('.js-ref-attach-input');
        var msg=root.querySelector('.js-ref-attach-msg');
        var code=(input&&input.value||'').trim();
        if(!code){ if(input) input.focus(); return; }
        var alabel=attachBtn.textContent; attachBtn.disabled=true; attachBtn.setAttribute('aria-busy','true'); attachBtn.textContent=attachBtn.dataset.busy||'Linking…';
        // Copy for the success + error states is editable per the refer
        // section's data-* attributes (FRONTEND.md: JS-rendered section copy →
        // settings → data-* → dataset), with these literals as the fallback.
        var dc=(msg&&msg.dataset)||{};
        api('/referral/attach',{ method:'POST', body:{ code:code } })
          .then(function(){
            attachBtn.removeAttribute('aria-busy'); attachBtn.textContent=alabel;
            if(msg) msg.textContent=dc.success||'✓ Linked! You both get your points after your first order.';
            try{ localStorage.removeItem(REF_KEY); }catch(e){}
          })
          .catch(function(e){
            var friendly={
              already_referred: dc.errAlready||'You’re already linked to a referrer; points are on the way.',
              self_referral: dc.errSelf||'That’s your own code; share it with a friend instead!',
              invalid_code: dc.errInvalid||'Hmm, we can’t find that code. Double-check it and try again.'
            }[e.code]||(dc.errGeneric||'That didn’t work. Give it another go.');
            if(msg) msg.textContent=friendly;
            attachBtn.disabled=false; attachBtn.removeAttribute('aria-busy'); attachBtn.textContent=alabel;
          });
      });
    }

    // ── Physical loyalty card (kkumi-club-card section) ─────────────────────
    // Hydrates the member's canonical KK- number and punch progress.
    function showCardLinked(card){
      var have=root.querySelector('.js-card-have');
      if(!have || !card) return;
      set('.js-card-code', card.display_code||card.code||'');
      have.removeAttribute('hidden');
    }
    // Stamp-card tracker: ✦-filled circles for earned punches, dashed empties
    // for the rest. per_card is server-clamped (1..50) so the row stays sane.
    function renderPunch(p){
      var block=root.querySelector('.js-punch-block'); if(!block||!p||!p.enabled) return;
      var stamps=block.querySelector('.js-punch-stamps');
      if(stamps){
        stamps.replaceChildren();
        var filled=Math.min(p.count||0, p.per_card||0);
        for(var i=0;i<(p.per_card||0);i++){
          var st=document.createElement('span');
          st.className='kk-clubcard__stamp'+(i<filled?' is-on':'');
          st.textContent=i<filled?'✦':'';
          stamps.appendChild(st);
        }
      }
      var prog=block.querySelector('.js-punch-progress');
      if(prog){
        var t=p.reward_label
          ? (prog.dataset.progress||'{count} of {total} stamps. Fill the card for {reward}.')
          : (prog.dataset.plain||'{count} of {total} stamps.');
        prog.textContent=t.split('{count}').join(p.count||0).split('{total}').join(p.per_card||0).split('{reward}').join(p.reward_label||'');
      }
      block.removeAttribute('hidden');
    }
    function loadCard(){
      var panel=root.querySelector('.js-card-panel'); if(!panel) return;
      function done(d){
        var loading=root.querySelector('.js-card-loading');
        if(loading) loading.setAttribute('hidden','');
        if(d) renderPunch(d.punch);
        if(d && d.card) showCardLinked(d.card);
      }
      api('/card').then(done).catch(function(){ done(null); });
    }

    var DOB_MONTHS=['January','February','March','April','May','June','July','August','September','October','November','December'];
    function showDobSet(mmdd){
      // Toggle via the `hidden` attribute, not style.display — the sitewide
      // .button rule has `display: inline-flex !important`, which beats any
      // inline display:none on the Add button. See .kkumi-club [hidden] in CSS.
      var add=root.querySelector('.js-dob-add'), form=root.querySelector('.js-dob-form'),
          chip=root.querySelector('.js-dob-chip'), st=root.querySelector('.js-dob-status');
      if(add) add.setAttribute('hidden','');
      if(form) form.setAttribute('hidden','');
      if(st) st.textContent='';
      if(chip){
        var p=String(mmdd).split('-');
        chip.replaceChildren();
        if(window.KKIcons) chip.appendChild(window.KKIcons.el('cake','js-dob-chip__icon'));
        chip.appendChild(document.createTextNode(' '+Number(p[1])+' '+(DOB_MONTHS[Number(p[0])-1]||'')));
        chip.removeAttribute('hidden');
      }
    }
    var dobAdd=root.querySelector('.js-dob-add');
    if(dobAdd){ dobAdd.addEventListener('click', function(){ dobAdd.setAttribute('hidden',''); var f=root.querySelector('.js-dob-form'); if(f) f.removeAttribute('hidden'); }); }
    var dobForm=root.querySelector('.js-dob-form');
    if(dobForm){
      dobForm.addEventListener('submit', function(e){
        e.preventDefault();
        var st=root.querySelector('.js-dob-status');
        var btn=dobForm.querySelector('button[type="submit"]');
        var m=dobForm.querySelector('.js-dob-month').value, d=dobForm.querySelector('.js-dob-day').value;
        if(!m||!d){ if(st) st.textContent=tpl('bday_pick_error','Pick a month and day.'); return; }
        // Saving state rides the button (not a status line under the form).
        if(st) st.textContent='';
        if(btn) window.KKBtn.busy(btn, tpl('bday_saving','Saving…'));
        api('/birthday', { method:'POST', body:{ month:Number(m), day:Number(d) } }).then(function(r){
          showDobSet(r.birthday); toast(tpl('bday_saved_toast','Birthday saved'));
        }).catch(function(e){
          if(btn) window.KKBtn.reset(btn);
          if(e.code==='birthday_locked'){ if(st) st.textContent=tpl('bday_locked_error','Your birthday is already set; contact us to change it.'); }
          else if(st){ st.textContent=tpl('bday_save_error','Couldn’t save. Please check the date.'); }
        });
      });
    }

    // Tier-progress "climb" rail. The CURRENT tier comes from /balance (so admin
    // tier-locks are respected). Everything the member sees is in POINTS — both
    // the pin (lifetime points earned) and the tier stops/thresholds (their
    // spend thresholds converted at the live earn rate). The bar uses lifetime
    // points within the current → next tier band. Icons come from the tiers
    // section's blocks so they always match; perks ("unlocks") are read the
    // same way and rendered as small SVG badges via the icon registry below.

    // Lightweight icon registry — Lucide-flavoured, 16×16, currentColor strokes.
    // Pick by name from a `<icon-name> | <text>` perk line (e.g.
    // "shipping | Free shipping, always"). Anything not matching falls back to
    // ✦. Keep additions short — this set covers what a loyalty program needs.
    var CLIMB_PERK_ICONS = {
      shipping: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5"/><path d="M14 17h2"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>',
      bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>',
      gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 7 12 8"/><path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 7 12 8"/></svg>',
      bolt: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
      star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9 12 2"/></svg>',
      heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.5-1.5 3-3.2 3-5.5 0-2.6-2.1-4.5-4.5-4.5-1.4 0-2.7.6-3.5 1.7C13.2 4.6 11.9 4 10.5 4 8.1 4 6 5.9 6 8.5c0 2.3 1.5 4 3 5.5l3 3Z"/></svg>',
      crown: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 17h20l-2-9-5 4-3-7-3 7-5-4-2 9Z"/><path d="M3 21h18"/></svg>',
      photocard: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/></svg>',
      box: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8 12 3 3 8v8l9 5 9-5Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>',
      clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
      support: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1v-7h3Z"/><path d="M3 19a2 2 0 0 0 2 2h1v-7H3Z"/></svg>',
      discount: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>',
      sparkle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg>',
      shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 3 6v6c0 5 3.5 8 9 10 5.5-2 9-5 9-10V6Z"/></svg>',
      check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    };
    var PERK_KEYWORDS = {
      shipping:'shipping', delivery:'shipping', ship:'shipping',
      bag:'bag', haul:'bag', shop:'bag',
      gift:'gift', bonus:'gift', welcome:'gift',
      bolt:'bolt', early:'bolt', drop:'bolt', fast:'bolt',
      star:'star', vip:'star', exclusive:'star', special:'star',
      heart:'heart', love:'heart', birthday:'heart', favourite:'heart', favorite:'heart',
      crown:'crown', top:'crown', idol:'crown', premium:'crown',
      photocard:'photocard', card:'photocard', photo:'photocard',
      box:'box', pack:'box', kit:'box',
      clock:'clock', priority:'clock',
      support:'support', help:'support', concierge:'support',
      discount:'discount', off:'discount', percent:'discount', sale:'discount',
      sparkle:'sparkle', shine:'sparkle', glow:'sparkle',
      shield:'shield', member:'shield', secure:'shield',
      check:'check', tick:'check', unlock:'check', access:'bolt',
    };
    function perkIcon(name){
      var key = String(name||'').toLowerCase().trim().replace(/[^a-z0-9]+/g,'');
      if (CLIMB_PERK_ICONS[key]) return CLIMB_PERK_ICONS[key];
      // Try a keyword guess against the perk text.
      var hit = PERK_KEYWORDS[key];
      if (hit && CLIMB_PERK_ICONS[hit]) return CLIMB_PERK_ICONS[hit];
      return null;
    }
    /**
     * Parse a single perk line into { icon, text }.
     *   "shipping | Free shipping, always" → icon: shipping SVG, text: "Free shipping, always"
     *   "Birthday double points"           → keyword-match on first word, fallback ✦
     */
    function parsePerk(line){
      var raw = String(line||'').trim();
      if (!raw) return null;
      var bar = raw.indexOf('|');
      if (bar >= 0) {
        var name = raw.slice(0, bar).trim();
        var text = raw.slice(bar+1).trim();
        return { svg: perkIcon(name), text: text || raw };
      }
      // No marker — guess from the first significant word.
      var firstWord = raw.split(/\s+/)[0];
      return { svg: perkIcon(firstWord), text: raw };
    }
    /** Read every perk line for a tier from the tiers section's rendered DOM. */
    function perksForTier(tierId){
      var spans = root.querySelectorAll('.kk-tier[data-tier="'+tierId+'"] .kk-tier__perks li > span');
      var out = [];
      spans.forEach(function(s){
        var t = (s.textContent || '').trim();
        if (!t) return;
        // Skip the rate row (we don't need "1 pt per $1 spent" as an unlock).
        if (s.querySelector('[data-kk-tier="rate"]')) return;
        var p = parsePerk(t);
        if (p) out.push(p);
      });
      return out;
    }
    function renderUnlocks(tier){
      var wrap = root.querySelector('.js-climb-unlocks'); if (!wrap) return;
      var perks = perksForTier(tier.id);
      if (!perks.length) { wrap.setAttribute('hidden',''); return; }
      set('.js-climb-unlockstier', tier.name);
      var list = root.querySelector('.js-climb-perks'); if (!list) return;
      list.innerHTML = perks.map(function(p){
        var icon = p.svg || '<span class="kk-climb__perk__star">✦</span>';
        return '<span class="kk-climb__perk">'+icon+'<span class="kk-climb__perk__t">'+esc(p.text)+'</span></span>';
      }).join('');
      wrap.removeAttribute('hidden');
    }

    function climbIcon(tierId){
      var el=root.querySelector('.kk-tier[data-tier="'+tierId+'"] .kk-tier__icon');
      var s=el && el.textContent ? el.textContent.trim() : '';
      return s ? esc(s) : '✦';
    }
    /** Is this the highest configured tier (nothing above it)? Drives the
     *  exclusive ribbon copy: top tier → "Idol only", otherwise "Rookie+".
     *  Uses /program tiers ordered by spend; false until /program lands (the
     *  grid re-renders when it does). */
    function isTopTier(tierId){
      if(!tierId || !lastProgram || !Array.isArray(lastProgram.tiers) || !lastProgram.tiers.length) return false;
      var t=lastProgram.tiers.slice().sort(function(a,c){ return (a.min_spend_cents||0)-(c.min_spend_cents||0); });
      return t[t.length-1].id===tierId;
    }
    /** Return the tier's icon as inline HTML (its rendered SVG markup) so it
     *  can be dropped into other widgets (e.g. the locked-reward overlay).
     *  Falls back to the ✦ glyph when the tiers section isn't on the page or
     *  the tier id is unknown. */
    function tierIconHtml(tierId){
      if(!tierId) return '✦';
      // Source order (server-set wins so admin edits propagate everywhere
      // without theme republishes):
      // 1) /program → tiers[].icon_svg (admin-pasted SVG in the rewards
      //    dashboard; mirrors to every consumer the moment the merchant saves).
      // 2) The rendered .kk-tier section's per-tier SVG (theme settings —
      //    the original source-of-truth, used as a fallback so legacy
      //    sites with no admin icon set still get the right glyph).
      // 3) The ✦ glyph (no tier section on the page and no admin SVG).
      if(lastProgram && Array.isArray(lastProgram.tiers)){
        for(var i=0;i<lastProgram.tiers.length;i++){
          if(lastProgram.tiers[i].id===tierId && lastProgram.tiers[i].icon_svg){
            return lastProgram.tiers[i].icon_svg;
          }
        }
      }
      var el=root.querySelector('.kk-tier[data-tier="'+tierId+'"] .kk-tier__icon');
      var html=el && el.innerHTML ? el.innerHTML.trim() : '';
      return html || '✦';
    }
    /** A tier's spend threshold in whole dollars. Used only for the bar-fill
     *  ratio (status/threshold — unitless, so dollars cancel). The customer-
     *  facing numbers are Polco Points (tierThresholdPoints); tier qualification
     *  is still spend-based, and bonuses never count toward tier. */
    function tierThresholdDollars(t){ return Math.round((t.min_spend_cents||0) / 100); }
    function fmt(n){ return Number(n||0).toLocaleString(); }
    function maybeClimb(){ if(lastProgram && lastBalanceObj){ buildClimb(lastProgram, lastBalanceObj); renderWalletTier(); } }

    // Cart wallet hero tier mini-progress (snippets/kkumi-cart-wallet.liquid).
    // Self-contained: bails unless the wallet's `.js-wallet-prog` is on the
    // page. Mirrors buildClimb's spend-band maths but renders a single thin
    // "$X to <NextTier>" bar instead of the full climb rail.
    function renderWalletTier(){
      var prog=root.querySelector('.js-wallet-prog'); if(!prog) return;
      var p=lastProgram, b=lastBalanceObj; if(!p||!b) return;
      var tiers=(p.tiers||[]).slice().sort(function(a,c){ return (a.min_spend_cents||0)-(c.min_spend_cents||0); });
      var n=tiers.length; if(!n){ prog.classList.remove('is-ready'); return; }
      // Work in CENTS so the readout is exact (e.g. "$1.05 to Rookie"). Existing
      // 12-month spend is floored to whole dollars — tier thresholds are
      // dollar-denominated and sub-dollar spend is just noise, so "$X to <Tier>"
      // is (round threshold − whole-dollar spend − the exact order). Without this
      // a member on $62.35 spend reads "$10.65" where the old bar showed "$11".
      var spendC=Math.floor(Math.max(0, b.spend_cents_12m||0)/100)*100;
      var tierId=b.tier && b.tier.id, ci=-1;
      if(tierId){ for(var j=0;j<n;j++){ if(tiers[j].id===tierId){ ci=j; break; } } }
      if(ci<0){ ci=0; for(var i=0;i<n;i++){ if(spendC>=(tiers[i].min_spend_cents||0)) ci=i; } }
      var next=ci<n-1?tiers[ci+1]:null;
      var fill=root.querySelector('.js-wallet-prog-fill');     // where they are now
      var fill2=root.querySelector('.js-wallet-prog-fill2');   // where they'll be after this order
      var label=root.querySelector('.js-wallet-tonext');
      // Current tier's icon, to the left of the bar.
      var progIc=root.querySelector('.js-wallet-prog-ic');
      if(progIc) progIc.innerHTML=tierIconHtml(tiers[ci] && tiers[ci].id);
      // Cents this order adds to tier spend (the eligible cart total). Tier
      // progress is spend-based, so this is the cart total — not points.
      var orderC=0;
      if(window.KkumiEarnPoints && typeof KkumiEarnPoints.cartCents==='function'){
        var oc=KkumiEarnPoints.cartCents();
        if(typeof oc==='number' && oc>0) orderC=oc;
      }
      // The tier's own icon comes from the rewards app (tierIconHtml prefers
      // /program tiers[].icon_svg) — never a hardcoded emoji.
      function tierIc(id){ return '<span class="kkumi-wallet__tonext-ic">'+tierIconHtml(id)+'</span>'; }
      function money(c){ return window.KKMoney.fromCents(c, { trim: true, shop: true }); }
      // Top tier has nothing left to fill, and a 100%-complete bar is the
      // single tallest thing in the wallet for zero information. `is-top`
      // collapses the track and renders the readout as a compact chip
      // instead (CSS: .kkumi-wallet__progress.is-top in kkumi-cart.css).
      prog.classList.toggle('is-top', !next);
      if(next){
        var curC=tiers[ci].min_spend_cents||0, nextC=next.min_spend_cents||0;
        var spanC=Math.max(1, nextC-curC);
        var afterC=spendC+orderC;
        var frac=Math.max(0, Math.min(1, (spendC-curC)/spanC));
        var afterFrac=Math.max(frac, Math.min(1, (afterC-curC)/spanC));
        if(fill) fill.style.width=Math.round(frac*100)+'%';
        if(fill2) fill2.style.width=Math.round(afterFrac*100)+'%';
        if(label){
          if(afterC>=nextC){
            // This order crosses the threshold.
            label.innerHTML=tpl('wallet_tonext_unlock','This order will unlock {tier}!',{tier:esc(next.name)})+' '+tierIc(next.id);
          } else {
            // Remaining AFTER this order's spend is applied.
            label.innerHTML=tpl('wallet_tonext_to','{amount} to {tier}',{amount:money(nextC-afterC),tier:esc(next.name)})+' '+tierIc(next.id);
          }
        }
      } else {
        if(fill) fill.style.width='100%';
        if(fill2) fill2.style.width='100%';
        if(label) label.innerHTML=tpl('wallet_top_reached','Top tier reached')+' '+tierIc(tiers[ci].id);
      }
      prog.classList.add('is-ready');
      prog.classList.remove('is-updating');
    }
    // A tier's spend threshold expressed as Polco Points (dollars × earn rate).
    // Tiers still qualify on spend; this is display-only. The rate is read live
    // off /program, never hardcoded.
    function tierThresholdPoints(t, earn){ return Math.round(((t.min_spend_cents||0) / 100) * (earn||1)); }
    function buildClimb(p, b){
      var rail=root.querySelector('.js-climb-rail'); if(!rail) return;
      var tiers=(p.tiers||[]).slice().sort(function(a,c){ return (a.min_spend_cents||0)-(c.min_spend_cents||0); });
      var n=tiers.length; if(!n) return;
      var earn=(p.points && p.points.earn_per_dollar) || 1;
      // Current tier comes from /balance so admin-locked tiers stick. Fall back to
      // the spend calc only if the id is missing or not in the program tier list.
      var spend=b.spend_cents_12m||0;
      var tierId=b.tier && b.tier.id;
      var ci=-1;
      if(tierId){ for(var j=0;j<n;j++){ if(tiers[j].id===tierId){ ci=j; break; } } }
      if(ci<0){ ci=0; for(var i=0;i<n;i++){ if(spend>=(tiers[i].min_spend_cents||0)) ci=i; } }
      var cur=tiers[ci], next=ci<n-1?tiers[ci+1]:null;
      var spendDollars = Math.floor(spend/100);
      // Tier Progress = status points (purchases only) = spend × rate.
      var statusPts = Math.round((spend/100) * earn);

      // Bar fill: position the member by DOLLARS SPENT within the current-tier →
      // next-tier spend band — the same measure tier promotion uses, so the bar
      // never overstates progress (bonus points don't count toward tier).
      var curT = tierThresholdDollars(cur);
      var nextT = next ? tierThresholdDollars(next) : curT;
      var frac = 1;
      if (next){
        var span = Math.max(1, nextT - curT);
        frac = Math.max(0, Math.min(1, (spendDollars - curT) / span));
      }
      var pct = n>1 ? Math.max(0, Math.min(100, (ci + frac) / (n-1) * 100)) : 100;

      var POLCO = tpl('polco_points_unit','Polco Points');
      set('.js-climb-tier', tpl('climb_tier','✦ {tier}',{tier:cur.name}));
      set('.js-climb-now', tpl('climb_now','your current tier · {n} {unit}',{n:fmt(statusPts),unit:POLCO}));
      set('.js-climb-pin', fmt(statusPts));
      if(next){
        var toNext = Math.max(0, tierThresholdPoints(next, earn) - statusPts);
        set('.js-climb-togo', fmt(toNext));
        set('.js-climb-togolabel', tpl('climb_togolabel','{unit} to {tier}',{unit:POLCO,tier:next.name}));
        // First perk of the next tier reads better as a teaser than "unlock X perks".
        var teaser = (perksForTier(next.id)[0] || {}).text || tpl('climb_teaser_fallback','{tier} perks',{tier:next.name});
        set('.js-climb-togosub', tpl('climb_togosub','unlock {perk}',{perk:teaser}));
      } else {
        set('.js-climb-togo', '✓');
        set('.js-climb-togolabel', tpl('climb_top_label','top tier reached'));
        set('.js-climb-togosub', tpl('climb_top_sub','you’re at the top, {tier} perks unlocked',{tier:cur.name}));
      }
      var fill=root.querySelector('.js-climb-fill'); if(fill) fill.style.setProperty('--p', pct+'%');
      var marker=root.querySelector('.js-climb-marker'); if(marker) marker.style.setProperty('--p', pct+'%');

      rail.querySelectorAll('.kk-climb__stop').forEach(function(el){ el.remove(); });
      tiers.forEach(function(t, idx){
        var x=n>1?(idx/(n-1)*100):50;
        var cls=idx<ci?'is-done':(idx===ci?'is-current':'is-next');
        var stop=document.createElement('div');
        stop.className='kk-climb__stop '+cls;
        stop.style.setProperty('--x', x+'%');
        stop.innerHTML='<span class="kk-climb__node">'+climbIcon(t.id)+'</span>'+
          '<span class="kk-climb__lbl"><span class="t">'+esc(t.name)+'</span><span class="s">'+fmt(tierThresholdPoints(t, earn))+'</span></span>';
        rail.appendChild(stop);
      });

      // "<NEXT> UNLOCKS" perk badges. When at top, advertise the top tier's
      // own perks (the member has unlocked them all). Pulled from the tiers
      // section's blocks so the two displays never drift.
      renderUnlocks(next || cur);
    }

    function applyProgram(p){
      lastProgram=p; maybeClimb();
      // Admin-saved tier SVGs override the tiers section's hard-coded glyphs.
      // Patch in place so the redeem grid's locked overlays (which call
      // tierIconHtml at render time) and the membercard tier badge (re-
      // rendered below) all pick up the same SVG. Trusted source (sanitised
      // by the worker), so innerHTML is OK here.
      if(Array.isArray(p.tiers)){
        p.tiers.forEach(function(t){
          if(!t || !t.icon_svg) return;
          root.querySelectorAll('.kk-tier[data-tier="'+t.id+'"] .kk-tier__icon').forEach(function(el){
            el.innerHTML = t.icon_svg;
          });
        });
      }
      // Re-render any tier badge already painted from /balance so it picks up
      // the admin SVG (badge was first rendered before /program landed).
      if(lastBalanceObj && lastBalanceObj.tier){
        renderTierBadge('.js-tier', lastBalanceObj.tier);
        renderTierBadgeAll('.js-redeem-tier', lastBalanceObj.tier);
      }
      var pt=p.points||{};
      var earn=pt.earn_per_dollar, step=pt.redeem_step, dollars=pt.redeem_dollars;
      REDEEM_STEP=step||100; REDEEM_DOLLARS=dollars||5; renderProgress(); renderWallet(); renderMoneyOff(); renderGrid();
      function w(v){ return v===1?'pt':'pts'; }
      function rate(mult){ return +(earn*(mult||1)).toFixed(2); }
      // VIP tiers can override the base birthday bonus. When we know the
      // member's current tier (from /balance), prefer the tier's value so the
      // "+pts on your birthday" copy on profile + ways-to-earn matches what
      // the cron will actually award.
      var memberTierId = lastBalanceObj && lastBalanceObj.tier && lastBalanceObj.tier.id;
      var bday = pt.birthday_bonus;
      if (memberTierId) {
        var pTiers = p.tiers || [];
        for (var bi=0; bi<pTiers.length; bi++) {
          if (pTiers[bi].id === memberTierId) {
            if (pTiers[bi].birthday_bonus) bday = pTiers[bi].birthday_bonus;
            break;
          }
        }
      }
      var R=rate(1);
      // The signed-in member's tier multiplier, once /balance has landed.
      var myMult=(lastBalanceObj && lastBalanceObj.tier && Number(lastBalanceObj.tier.multiplier)) || 1;
      if(!(myMult>0)) myMult=1;
      // Deploy-order safety: the theme and the rewards worker ship separately,
      // so tolerate a worker still on the pre-2026-07-25 `signup_bonus` key
      // rather than rendering "+undefined points" during the window.
      var so=pt.second_order_bonus!=null?pt.second_order_bonus:pt.signup_bonus;
      var T={
        'per-points':'$'+dollars,
        'per-points-label':tpl('token_per_points_label','per {step} points',{step:step}),
        // Welcome points. They are credited on the member's SECOND paid order
        // (they used to land at sign-up — the join perk is now the BOGO), so
        // the token names say second-order. The old `signup-*` names are kept
        // as aliases below for copy written before the rule changed.
        'second-order-points':'+'+so+' points',
        'second-order-welcome':tpl('token_signup_welcome','+{n} welcome points',{n:so}),
        'second-order-pts':'+'+so+' pts',
        'second-order-n':''+so,
        'birthday-pts':'+'+bday+' pts',
        'referral-pts':'+'+pt.referral_bonus+' pts',
        'review-pts':'+'+(pt.review_bonus||0)+' pts',
        // Number-only variants for the earn ladder's big-number + small-unit layout.
        'earn-n':''+R,
        'second-order-amt':'+'+fmt(so),
        'birthday-amt':'+'+fmt(bday),
        'review-amt':'+'+fmt(pt.review_bonus||0),
        'referral-num':fmt(pt.referral_bonus),
        'referral-amt':'+'+fmt(pt.referral_bonus),
        'earn':tpl('token_earn_per_dollar','{n} {unit} per $1 spent',{n:R,unit:w(R)}),
        // The rate THIS member is actually on (base x their tier multiplier),
        // for copy shown to signed-in members — the base-rate `earn` token
        // understates it for every tier above the baseline. Falls back to the
        // base rate until /balance lands (applyProgram re-runs then).
        'earn-mine':tpl('token_earn_per_dollar','{n} {unit} per $1 spent',{n:rate(myMult),unit:w(rate(myMult))}),
        'earn-plus':'+'+R+' '+w(R)+' / $1',
        // Lowest to highest tier rate ("1–1.5"), for the ways-to-earn order row.
        'earn-range':(function(){
          var ms=(p.tiers||[]).map(function(t){ return Number(t.multiplier)||1; });
          if(!ms.length) return ''+R;
          var lo=rate(Math.min.apply(null,ms)), hi=rate(Math.max.apply(null,ms));
          return lo===hi ? ''+lo : lo+'–'+hi;
        })(),
        'earn-point':tpl('token_earn_point','{n} {unit} per $1 spent',{n:R,unit:'point'+(R===1?'':'s')}),
        'redeem':tpl('token_redeem','{step} pts = {amount} off',{step:step,amount:'$'+dollars}),
        'redeem-eq':tpl('token_redeem_eq','{step} points = {amount}',{step:step,amount:'$'+dollars}),
        'redeem-words':tpl('token_redeem_words','{step} points equals {amount} off',{step:step,amount:'$'+dollars}),
        'redeem-step-pts':step+' pts',
        'redeem-step-val':tpl('token_redeem_step_val','{amount} OFF',{amount:'$'+dollars}),
        // Number-only twins of the two above, for the footer club card's
        // big-number + small-caption layout (mega-footer's `points_label` /
        // `value_label` supply the unit words — "1000 points", "$5 credit").
        // Baking a unit into the number there rendered "1000 pts POINTS".
        'redeem-step-n':''+step,
        'redeem-step-amt':'$'+dollars
      };
      // Legacy aliases: copy authored before the welcome points moved to the
      // second order still says data-kk="signup-…". Bind them to the same live
      // value so it can never render a stale hardcoded number. New copy should
      // use the second-order-* names.
      T['signup-points']=T['second-order-points'];
      T['signup-welcome']=T['second-order-welcome'];
      T['signup-pts']=T['second-order-pts'];
      T['signup-n']=T['second-order-n'];
      T['signup-amt']=T['second-order-amt'];
      Object.keys(T).forEach(function(k){
        root.querySelectorAll('[data-kk="'+k+'"]').forEach(function(el){ el.textContent=T[k]; });
      });
      if(typeof p.members==='number'){ root.querySelectorAll('[data-kk="members"]').forEach(function(el){ el.textContent=p.members.toLocaleString(); }); }
      root.querySelectorAll('[data-kk="tiers-count"]').forEach(function(el){ el.textContent=(p.tiers||[]).length; });
      (p.tiers||[]).forEach(function(t){
        // Standalone tier-name tokens (e.g. the account gate's tier ladder).
        root.querySelectorAll('[data-kk-tier-name="'+t.id+'"]').forEach(function(el){ el.textContent=t.name; });
        // v11 tier cards (snippets/kkumi-club-tier-card.liquid + the Tiers
        // section): the absolute earn rate for this tier ("1.25"), the admin's
        // tier SVG (sanitised by the worker — trusted, like the .kk-tier__icon
        // patch above) and the spend gate. The gate's two wordings ride the
        // element ("Free to join" / "Spend {amount}"), so the copy stays a
        // merchant setting; the amount is the shop-currency min_spend_cents.
        root.querySelectorAll('[data-kk-tier-rate="'+t.id+'"]').forEach(function(el){ el.textContent=''+rate(t.multiplier); });
        if(t.icon_svg){ root.querySelectorAll('[data-kk-tier-icon="'+t.id+'"]').forEach(function(el){ el.innerHTML=t.icon_svg; }); }
        root.querySelectorAll('[data-kk-tier-gate="'+t.id+'"]').forEach(function(el){
          var min=Number(t.min_spend_cents)||0;
          var amt=window.KKMoney ? window.KKMoney.fromCents(min,{shop:true,trim:true}) : String(Math.round(min/100));
          var ptsEq=fmt(Math.round((min/100)*(earn||1)));
          el.textContent = min>0 ? (el.getAttribute('data-spend')||'Spend [amount]').replace(/\[amount\]|\{amount\}/g, amt).split('{points}').join(ptsEq) : (el.getAttribute('data-free')||'Free to join');
        });
        var card=root.querySelector('.kk-tier[data-tier="'+t.id+'"]'); if(!card) return;
        var n=card.querySelector('[data-kk-tier="name"]'); if(n) n.textContent=t.name;
        var th=card.querySelector('[data-kk-tier="thresh"]'); if(th) th.textContent=tpl('tier_thresh_unit','{n}+ {unit}',{n:fmt(Math.round(((t.min_spend_cents||0)/100)*(earn||1))),unit:tpl('polco_points_unit','Polco Points')});
        var rt=card.querySelector('[data-kk-tier="rate"]');
        if (rt) {
          // For non-baseline tiers, express the tier perk as a RELATIVE gain
          // ("25% more points", "50% more points") rather than the absolute
          // computed rate — easier to compare across tiers at a glance.
          // Baseline tier (multiplier 1) keeps the absolute "<n> pts per $1
          // spent" framing since "+0% more" doesn't communicate anything.
          var mult = Number(t.multiplier) || 1;
          var parent = rt.parentNode;
          parent.textContent = '';
          if (mult > 1) {
            var b = document.createElement('b');
            b.setAttribute('data-kk-tier', 'rate');
            b.textContent = tpl('tier_rate_more','{pct}% more points',{pct:Math.round((mult - 1) * 100)});
            parent.appendChild(b);
          } else {
            var r = rate(mult);
            // Bold the live "<n> <unit>" fragment; the surrounding template stays
            // merchant-editable. The <b> keeps data-kk-tier="rate" so the next
            // applyProgram() re-run still finds + rebuilds this row.
            parent.innerHTML = tpl('tier_rate_baseline','{rate} per $1 spent',{rate:'<b data-kk-tier="rate">'+r+' '+w(r)+'</b>'});
          }
        }
        var bd=card.querySelector('[data-kk-tier="birthday"]'); if(bd){ var bb=(t.birthday_bonus!=null?t.birthday_bonus:pt.birthday_bonus)||0; bd.textContent='+'+fmt(bb)+' pts'; }
        // Perk lines: swap {token}s for live values in JS (same binder as above) —
        // the raw text is stashed in data-kkraw so re-runs stay idempotent.
        var pbb=(t.birthday_bonus!=null?t.birthday_bonus:pt.birthday_bonus)||0, pr=rate(t.multiplier);
        // {rate} token: same baseline-vs-bonus split as the rate row above.
        var pmult = Number(t.multiplier) || 1;
        var rateLabel = pmult > 1
          ? tpl('tier_rate_more','{pct}% more points',{pct:Math.round((pmult - 1) * 100)})
          : tpl('tier_rate_baseline','{rate} per $1 spent',{rate:pr+' '+w(pr)});
        card.querySelectorAll('[data-kk-perk]').forEach(function(el){
          var raw=el.getAttribute('data-kkraw'); if(raw===null){ raw=el.textContent; el.setAttribute('data-kkraw',raw); }
          el.textContent=raw
            .replace(/\{second_order\}|\{signup\}/g,'+'+fmt(so)+' pts')
            .replace(/\{birthday\}/g,'+'+fmt(pbb)+' pts')
            .replace(/\{referral\}/g,'+'+fmt(pt.referral_bonus)+' pts')
            .replace(/\{review\}/g,'+'+fmt(pt.review_bonus||0)+' pts')
            .replace(/\{rate\}/g, rateLabel);
        });
      });
    }
    // ── Cart wallet loading state ─────────────────────────────────────────
    // The wallet hero (snippets/kkumi-cart-wallet.liquid) shows ONLY a
    // centered "Loading rewards…" spinner until every source it depends on
    // has resolved, then reveals its content. No-op when the wallet isn't on
    // the page. A safety-net timeout means a failed endpoint never hangs it.
    var WalletLoad = (function(){
      var el = root.querySelector('.kkumi-wallet.is-loading');
      if(!el) return { done: function(){} };
      var flags = { program:false, options:false, balance:!LOGGED_IN, earn:false };
      var revealed = false;
      function reveal(){ if(revealed) return; revealed = true; el.classList.remove('is-loading'); }
      function check(){ if(flags.program && flags.options && flags.balance && flags.earn) reveal(); }
      // The earn line drops `.is-loading` once kkumi-earn-points.js resolves
      // the rate — watch the class (and read the initial state).
      var earnEl = el.querySelector('.kkumi-earn-points');
      function checkEarn(){ if(!earnEl || !earnEl.classList.contains('is-loading')){ flags.earn = true; check(); } }
      if(earnEl) new MutationObserver(checkEarn).observe(earnEl, { attributes:true, attributeFilter:['class'] });
      checkEarn();
      setTimeout(reveal, 8000);
      return { done: function(k){ if(k in flags && !flags[k]){ flags[k] = true; check(); } } };
    })();
    window.__kkWalletLoad = WalletLoad;

    // Re-project the wallet tier bar when the eligible cart total changes
    // (kkumi-earn-points.js fires this after each cart mutation). Safe before
    // load — renderWalletTier bails until program + balance are in.
    document.addEventListener('kkumi:cart-total', renderWalletTier);

    // Mirror the free-shipping bar's "updating" shimmer on the tier bar while
    // a cart mutation is in flight: add it on the interaction, renderWalletTier
    // clears it when the new total lands.
    function markWalletUpdating(){ var pr=root.querySelector('.js-wallet-prog'); if(pr) pr.classList.add('is-updating'); }
    document.addEventListener('change', function(e){
      if(e.target && e.target.matches && e.target.matches('.cart-quantity-input, [name="updates[]"], input[type="number"][data-quantity-variant-id]')) markWalletUpdating();
    });
    document.addEventListener('click', function(e){
      if(e.target.closest && e.target.closest('.quantity__button, cart-remove-button, .kkumi-qty__btn')) markWalletUpdating();
    });

    api('/program').then(applyProgram).catch(function(){}).then(function(){ WalletLoad.done('program'); });

    if(!IS_CLUB_PAGE){
      // Footer-only pages stop after /program — EXCEPT when a token on the page
      // is tier-scoped. `earn-mine` (the footer club card's signed-in blurb) and
      // the birthday twins resolve their value from lastBalanceObj, so without
      // /balance they silently fall back to the BASE rate/bonus: an Idol on a
      // 1.5x tier read the program's 10/$1 in the footer of every page except
      // the club page, where loadMember() happens to fetch it. Cheap — the
      // shared cache version-gates member reads on a single /balance probe per
      // page load, and the club launcher already warms it for members.
      // applyProgram() covers either resolution order: if /balance lands first
      // lastProgram is still null and the /program handler picks the tier up.
      if(LOGGED_IN && root.querySelector('[data-kk="earn-mine"],[data-kk="birthday-pts"],[data-kk="birthday-amt"]')){
        api('/balance').then(function(b){
          if(!b || !b.logged_in) return;
          lastBalanceObj = b;
          if(lastProgram) applyProgram(lastProgram);
        }).catch(function(){});
      }
      return;
    }
    // loadReferral runs from loadMember once /balance confirms club membership
    // (a non-member has no share link — the worker answers 403 club_required).
    if(LOGGED_IN){ loadMember(); loadCard(); }
    loadOptions();
    initJoinBar();

    // ── Phone join bar (kkumi-club-cta, guests) ─────────────────────────────
    // Shows under 900px once the hero's join buttons have scrolled off the
    // TOP (never while they're still further down), and steps the club
    // launcher / promo card aside via body.kk-joinbar-on — the same rule the
    // PDP sticky buy bar uses. Without the hero on the page it shows as soon
    // as the visitor scrolls.
    function initJoinBar(){
      var bar=root.querySelector('[data-kk-joinbar]');
      if(!bar) return;
      var mq=window.matchMedia('(max-width: 899px)');
      var target=root.querySelector('.kk-fanhero__ctas, .kk-hero__ctas');
      var ctaInView=!!target;
      function apply(){
        var on=mq.matches && !ctaInView && (target || window.scrollY>200);
        bar.classList.toggle('is-visible', !!on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        document.body.classList.toggle('kk-joinbar-on', !!on);
      }
      bar.hidden=false;
      if(target && 'IntersectionObserver' in window){
        new IntersectionObserver(function(entries){
          var e=entries[entries.length-1];
          ctaInView = e.isIntersecting || e.boundingClientRect.top > 0;
          apply();
        }).observe(target);
      } else {
        ctaInView=false;
        window.addEventListener('scroll', apply, { passive:true });
      }
      if(mq.addEventListener) mq.addEventListener('change', apply); else if(mq.addListener) mq.addListener(apply);
      apply();
    }

    // The twin of announcePointsChanged(): a redemption made in the club
    // launcher (its own runtime, its own state) moves the same balance these
    // sections are rendering, and the cart wallet / club page would otherwise
    // keep the old number until a reload. Refetch only — never re-announce, or
    // the two surfaces would trigger each other forever.
    if(LOGGED_IN){
      document.addEventListener('kkumi:points-changed', function(e){
        // Our own announce already refetched — only react to other surfaces.
        if(e && e.detail && e.detail.source === 'club') return;
        if(window.KKRewards) window.KKRewards.invalidate();
        loadMember(); loadOptions();
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
